import PendingStockOperationNotice from "../../components/stock/PendingStockOperationNotice";
// src/pages/Stock/StockOverviewPage.jsx
//
// Stock Overview Page
// =============================================================================
//
// PURPOSE
// -------
// First frontend entry point for the planned stock / warehouse / inventory
// module.
//
// This page intentionally keeps product catalog and inventory responsibilities
// separated:
// - product catalog owns SKU/product master data,
// - stock module owns locations, balances, movements and valuation.
//
// PHASE 1 SCOPE
// -------------
// This is an operational foundation page, not the final reception sale module.
// It allows the team to post basic movements and verify the backend valuation
// logic before sales, online shop stock consumption or service-product
// consumption are connected.
//
// VALUATION DISPLAY
// -----------------
// The shown average cost is the global product moving average from the backend.
// It is not a separate location average. Location value is derived from physical
// quantity in that location multiplied by the global average cost.

import { Link } from "react-router-dom";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import ProductPicker from "../../components/catalog/pickers/ProductPicker";
import PaginationFooter from "../../components/common/PaginationFooter";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";
import {
  fetchStockBalances,
  fetchStockBatchBalances,
  fetchStockBatches,
  fetchStockLocations,
  fetchStockMovements,
  fetchStockWorkspaceProducts,
  fetchStockProductContext,
  searchStockProducts,
  STOCK_MOVEMENT_TYPES,
  receiveStock,
  issueStock,
  transferStock,
  correctStock,
} from "../../services/stockService";
import { isRequestCanceled } from "../../services/httpUtils";
import { movementTypeLabel } from "../../utils/i18nLabels";
import AuthContext from "../../context/AuthContext";
import StockPostingDialog from "./components/StockPostingDialog";
import StockMovementDetailDialog from "./components/StockMovementDetailDialog";
import StockMovementCorrectionDialog from "./components/StockMovementCorrectionDialog";
import StockValueAdjustmentDialog from "./components/StockValueAdjustmentDialog";
import StockMovementFilters from "./components/StockMovementFilters";
import StockBalanceRowActions from "./components/StockBalanceRowActions";
import StockFormSection from "./components/StockFormSection";
import StockPostingResultSummary from "./components/StockPostingResultSummary";
import StockSummaryCards from "./components/StockSummaryCards";
import StockProductBrowser from "./components/StockProductBrowser";
import StockProductWorkspace from "./components/StockProductWorkspace";
import StockViewTabs from "./components/StockViewTabs";

import "./StockOverviewPage.css";

const ACTION_RECEIVE = "receive";
const ACTION_ISSUE = "issue";
const ACTION_TRANSFER = "transfer";
const ACTION_CORRECTION = "correction";

const VIEW_PRODUCT_WORKSPACE = "product_workspace";
const VIEW_BALANCES = "balances";
const VIEW_BATCHES = "batches";
const VIEW_MOVEMENTS = "movements";

const WORKSPACE_PRODUCT_PAGE_SIZE_OPTIONS = Object.freeze([25, 50, 100]);
const DEFAULT_WORKSPACE_PRODUCT_PAGE_SIZE = 25;
const DEFAULT_WORKSPACE_DETAIL_PAGE_SIZE = 25;

const STOCK_PAGE_SIZE_OPTIONS = Object.freeze([25, 50, 100]);
const DEFAULT_STOCK_PAGE_SIZE = 50;
const MOVEMENT_PERIOD_MODES = Object.freeze([
  EVALUATION_PERIOD_TYPES.MONTH,
  EVALUATION_PERIOD_TYPES.THREE_MONTHS,
  EVALUATION_PERIOD_TYPES.SIX_MONTHS,
  EVALUATION_PERIOD_TYPES.YEAR,
  EVALUATION_PERIOD_TYPES.WEEK,
  EVALUATION_PERIOD_TYPES.DAY,
  EVALUATION_PERIOD_TYPES.MANUAL,
]);

function createEmptyPagination(pageSize = DEFAULT_STOCK_PAGE_SIZE) {
  return { page: 1, page_size: pageSize, total: 0, page_count: 1 };
}

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

function todayDateOnlyLocal() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const RECEIPT_QUANTITY_MODES = Object.freeze({
  STOCK_UNIT: "stock_unit",
  PACKAGE: "package",
});

const COST_ENTRY_MODES = Object.freeze({
  STOCK_UNIT: "stock_unit",
  PACKAGE: "package",
  TOTAL: "total",
});

function formatDecimal(value, digits = 3) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(digits).replace(/\.?0+$/, "");
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)} PLN` : String(value);
}

function parseDecimalInput(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function formatDateTime(value, language = "en") {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  // Date/time formatting is presentation-only. Keep the backend timestamp
  // unchanged and format it according to the currently displayed UI language.
  const locale = String(language || "en").startsWith("pl") ? "pl-PL" : "en-GB";

  return d.toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateOnly(value, language = "en") {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);

  const locale = String(language || "en").startsWith("pl") ? "pl-PL" : "en-GB";
  return d.toLocaleDateString(locale, { year: "numeric", month: "2-digit", day: "2-digit" });
}

function productLabel(product) {
  if (!product) return "—";
  const parts = [product.name_public, product.variant_label, product.sku ? `SKU: ${product.sku}` : null]
    .filter(Boolean)
    .map(String);
  return parts.join(" · ");
}

function getProductUnit(product) {
  return product?.stockUnit?.symbol || product?.stock_unit_code || "";
}

function getMovementUnit(movement) {
  // Prefer the historical movement unit. It was copied from
  // Product.stock_unit_code at posting time, so old movement history remains
  // readable even if product master data is changed later.
  return movement?.stock_unit_code || getProductUnit(movement?.product);
}


function getUnitCode(value) {
  return String(value || "").trim().toLowerCase();
}

const CLIENT_UNIT_CONVERSIONS = Object.freeze({
  "l->ml": 1000,
  "ml->l": 0.001,
  "kg->g": 1000,
  "g->kg": 0.001,
});

function getClientConversionFactor(fromUnitCode, toUnitCode) {
  const from = getUnitCode(fromUnitCode);
  const to = getUnitCode(toUnitCode);
  if (!from || !to) return null;
  if (from === to) return 1;
  return CLIENT_UNIT_CONVERSIONS[`${from}->${to}`] || null;
}

function getProductContentUnit(product) {
  return product?.contentUnit?.symbol || product?.content_unit_code || "";
}

function getProductPackageLabel(product, t) {
  return product?.packaging_label || t("stock:packages.defaultPackageLabel");
}

function calculatePackageStockQuantity(product, packageQuantity) {
  if (!product || packageQuantity === "" || packageQuantity === null || packageQuantity === undefined) return null;

  const packages = Number(String(packageQuantity).replace(",", "."));
  const contentQty = Number(product?.content_quantity);
  const contentUnit = product?.content_unit_code;
  const stockUnit = product?.stock_unit_code || "pcs";
  const factor = getClientConversionFactor(contentUnit, stockUnit);

  if (!Number.isFinite(packages) || packages <= 0) return null;
  if (!Number.isFinite(contentQty) || contentQty <= 0) return null;
  if (!factor) return null;

  return packages * contentQty * factor;
}

function calculatePackageQuantityFromStockQuantity(product, stockQuantity) {
  const quantity = parseDecimalInput(stockQuantity);
  const stockPerPackage = calculatePackageStockQuantity(product, 1);

  if (quantity === null || quantity <= 0) return null;
  if (stockPerPackage === null || stockPerPackage <= 0) return null;

  return quantity / stockPerPackage;
}

function hasPackageDefinition(product) {
  const contentQty = Number(product?.content_quantity);
  return Boolean(product?.content_unit_code && product?.stock_unit_code && Number.isFinite(contentQty) && contentQty > 0);
}

function createEmptyReceiptBatch() {
  return {
    batch_number: "",
    expiry_date: "",
    package_quantity: "",
    quantity: "",
    comment: "",
  };
}

function asBoolean(value, defaultValue = false) {
  if (value === undefined || value === null || value === "") return defaultValue;
  if (value === true || value === 1 || value === "1") return true;
  const normalized = String(value).trim().toLowerCase();
  return normalized === "true" || normalized === "yes" || normalized === "on";
}

function productRequiresBatchOnReceipt(product) {
  return asBoolean(product?.requires_batch_on_receipt, false);
}

function productRequiresExpiryOnReceipt(product) {
  return asBoolean(product?.requires_expiry_on_receipt, false);
}

function createInitialStockForm(product = null) {
  return {
    product_id: product?.id ? String(product.id) : "",
    business_date: todayDateOnlyLocal(),
    location_id: "",
    from_location_id: "",
    to_location_id: "",
    quantity: "",
    quantity_delta: "",
    unit_cost_net: "",
    reason: "",
    comment: "",
    batch_number: "",
    expiry_date: "",
    supplier_name: "",
    receipt_document_no: "",
    receipt_batches: [createEmptyReceiptBatch()],
    receipt_quantity_mode: product && hasPackageDefinition(product)
      ? RECEIPT_QUANTITY_MODES.PACKAGE
      : RECEIPT_QUANTITY_MODES.STOCK_UNIT,
    package_quantity: "",
    cost_entry_mode: COST_ENTRY_MODES.STOCK_UNIT,
    package_unit_cost_net: "",
    receipt_total_cost_net: "",
    batch_policy: product?.default_issue_batch_policy || "none",
  };
}

function createContextPostingForm(action, product = null, locationId = "") {
  const next = createInitialStockForm(product);
  const normalizedLocationId = locationId ? String(locationId) : "";

  if (action === ACTION_TRANSFER) next.from_location_id = normalizedLocationId;
  else next.location_id = normalizedLocationId;

  return next;
}

function serializePostingDraft(selectedProduct, form) {
  return JSON.stringify({
    product_id: selectedProduct?.id ? String(selectedProduct.id) : "",
    form,
  });
}

function calculateReceiptCostValues(product, form) {
  const packageMode = form.receipt_quantity_mode === RECEIPT_QUANTITY_MODES.PACKAGE;
  const stockQuantity = packageMode
    ? calculatePackageStockQuantity(product, form.package_quantity)
    : parseDecimalInput(form.quantity);

  if (!stockQuantity || stockQuantity <= 0) return null;

  const packageQuantity = packageMode
    ? parseDecimalInput(form.package_quantity)
    : calculatePackageQuantityFromStockQuantity(product, stockQuantity);

  const mode = form.cost_entry_mode || COST_ENTRY_MODES.STOCK_UNIT;
  let total = null;
  let unitCost = null;
  let packageUnitCost = null;

  if (mode === COST_ENTRY_MODES.PACKAGE) {
    packageUnitCost = parseDecimalInput(form.package_unit_cost_net);
    if (!packageQuantity || packageQuantity <= 0 || packageUnitCost === null) return null;
    total = packageQuantity * packageUnitCost;
    unitCost = total / stockQuantity;
  } else if (mode === COST_ENTRY_MODES.TOTAL) {
    total = parseDecimalInput(form.receipt_total_cost_net);
    if (total === null) return null;
    unitCost = total / stockQuantity;
    if (packageQuantity && packageQuantity > 0) packageUnitCost = total / packageQuantity;
  } else {
    unitCost = parseDecimalInput(form.unit_cost_net);
    if (unitCost === null) return null;
    total = stockQuantity * unitCost;
    if (packageQuantity && packageQuantity > 0) packageUnitCost = total / packageQuantity;
  }

  return { stockQuantity, packageQuantity, total, unitCost, packageUnitCost };
}

function formatProductPackage(product, t) {
  if (!product) return "—";

  const label = getProductPackageLabel(product, t);
  const contentQty = product?.content_quantity;
  const contentUnit = getProductContentUnit(product) || product?.content_unit_code;

  if (!contentQty || !contentUnit) return label;

  return `${label} ${formatDecimal(contentQty, 3)} ${contentUnit}`;
}

function formatBatchPackageEquivalent(row, t) {
  const product = row?.product;
  const quantity = Number(row?.quantity);
  const contentQty = Number(product?.content_quantity);
  const contentUnit = product?.content_unit_code;
  const stockUnit = row?.stock_unit_code || product?.stock_unit_code;
  const factor = getClientConversionFactor(contentUnit, stockUnit);

  if (!Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(contentQty) || contentQty <= 0 || !factor) {
    return "—";
  }

  const stockPerPackage = contentQty * factor;
  if (!stockPerPackage) return "—";

  const packages = quantity / stockPerPackage;
  return `${formatDecimal(packages, 3)} × ${formatProductPackage(product, t)}`;
}

function formatMovementBatchSummary(row) {
  const allocations = Array.isArray(row?.batchAllocations) ? row.batchAllocations : [];
  if (allocations.length === 0) return "—";

  const labels = allocations
    .slice(0, 2)
    .map((allocation) => allocation.batch?.batch_number || `#${allocation.batch_id}`);

  if (allocations.length > 2) labels.push(`+${allocations.length - 2}`);
  return labels.join(" · ");
}

function formatMovementUser(row) {
  const user = row?.user;
  if (!user) return "—";
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return fullName || user.username || `#${user.id}`;
}

function movementLabel(t, type) {
  return movementTypeLabel(t, type);
}

function movementDirection(row) {
  const hasFrom = row?.from_location_id !== null && row?.from_location_id !== undefined;
  const hasTo = row?.to_location_id !== null && row?.to_location_id !== undefined;

  if (hasFrom && hasTo) return { kind: "transfer", prefix: "↔" };
  if (hasTo && !hasFrom) return { kind: "positive", prefix: "+" };
  if (hasFrom && !hasTo) return { kind: "negative", prefix: "−" };

  const delta = Number(row?.product_quantity_delta);
  if (Number.isFinite(delta) && delta > 0) return { kind: "positive", prefix: "+" };
  if (Number.isFinite(delta) && delta < 0) return { kind: "negative", prefix: "−" };
  return { kind: "neutral", prefix: "" };
}

function getTraceBatchAllocation(row, batchId) {
  if (!batchId) return null;
  const allocations = Array.isArray(row?.batchAllocations) ? row.batchAllocations : [];
  const matching = allocations.filter((allocation) => String(allocation.batch_id) === String(batchId));
  if (!matching.length) return null;

  const totals = matching.reduce((acc, allocation) => ({
    quantity: acc.quantity + Number(allocation.quantity || 0),
    totalCost: acc.totalCost + Number(allocation.total_cost_net || 0),
  }), { quantity: 0, totalCost: 0 });

  return {
    ...totals,
    unitCost: totals.quantity > 0 ? totals.totalCost / totals.quantity : null,
  };
}

function dateOnlyFromValue(value) {
  if (!value) return "";
  const raw = String(value);
  const direct = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (direct) return direct[1];
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

function buildPostingResultContext({ action, product, form, result, locations }) {
  const movement = result?.movement || null;
  const productCost = result?.productCost || null;
  if (!movement) return null;

  const locationName = (id) => locations.find((location) => String(location.id) === String(id))?.name || `#${id}`;
  const unit = movement.stock_unit_code || getProductUnit(product) || product?.stock_unit_code || "";
  const direction = movementDirection(movement);
  const locationBalances = [];

  if (action === ACTION_TRANSFER) {
    const fromId = movement.from_location_id || form.from_location_id;
    const toId = movement.to_location_id || form.to_location_id;
    if (result?.fromBalance) {
      locationBalances.push({
        id: fromId,
        role: "from",
        name: locationName(fromId),
        quantity: result.fromBalance.quantity,
      });
    }
    if (result?.toBalance) {
      locationBalances.push({
        id: toId,
        role: "to",
        name: locationName(toId),
        quantity: result.toBalance.quantity,
      });
    }
  } else if (result?.balance) {
    const locationId = movement.to_location_id || movement.from_location_id || form.location_id;
    locationBalances.push({
      id: locationId,
      role: "location",
      name: locationName(locationId),
      quantity: result.balance.quantity,
    });
  }

  return {
    action,
    movementId: movement.id,
    businessDate: movement.business_date || form.business_date,
    product: product || null,
    productName: product?.name_public || "",
    unit,
    quantity: movement.quantity,
    changeKind: direction.kind,
    changePrefix: direction.prefix,
    locationBalances,
    productQuantityAfter: productCost?.total_quantity,
    averageCostAfter: productCost?.average_cost_net,
    postingContext: result?.posting_context || null,
    productValueAfter: productCost?.total_value_net,
  };
}

function StockOverviewPage() {
  const { t, i18n } = useTranslation(["common", "navigation", "stock"]);
  const { authState } = useContext(AuthContext);
  const currentRole = String(authState?.user?.role || "user").trim().toLowerCase();
  const canPostStock = currentRole === "superuser" || currentRole === "reception";
  const canCorrectRecordedMovement = currentRole === "superuser";
  const movementPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    allowedModes: MOVEMENT_PERIOD_MODES,
  });
  const workspaceMovementPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    allowedModes: MOVEMENT_PERIOD_MODES,
  });
  // Master/current-state data loaded from the backend. Product master data is
  // searched on demand through ProductPicker instead of being loaded into a
  // large native dropdown. Batch balances and movement history are lazy-loaded
  // only when their cockpit tabs are opened.
  const [locations, setLocations] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [balances, setBalances] = useState([]);
  const [batchBalances, setBatchBalances] = useState([]);
  const [movements, setMovements] = useState([]);
  const [activeView, setActiveView] = useState(VIEW_PRODUCT_WORKSPACE);
  const [postingOpen, setPostingOpen] = useState(false);
  const [valueAdjustmentOpen, setValueAdjustmentOpen] = useState(false);

  // Product-centric workspace: all three browsers are server-paginated.
  // LOT and ledger pages have independent filters and counts; they remain in
  // one product read snapshot, not separate requests with mixed valuations.
  const [workspaceProducts, setWorkspaceProducts] = useState([]);
  const [workspaceProductPagination, setWorkspaceProductPagination] = useState(() => createEmptyPagination(DEFAULT_WORKSPACE_PRODUCT_PAGE_SIZE));
  const [workspaceProductPage, setWorkspaceProductPage] = useState(1);
  const [workspaceProductPageSize, setWorkspaceProductPageSize] = useState(DEFAULT_WORKSPACE_PRODUCT_PAGE_SIZE);
  const [workspaceProductQuery, setWorkspaceProductQuery] = useState("");
  const debouncedWorkspaceProductQuery = useDebouncedValue(workspaceProductQuery, 300);
  const [workspaceLocationFilter, setWorkspaceLocationFilter] = useState("");
  const [workspaceOnlyWithStock, setWorkspaceOnlyWithStock] = useState(false);
  const [activeWorkspaceProduct, setActiveWorkspaceProduct] = useState(null);
  const [workspaceBatchRows, setWorkspaceBatchRows] = useState([]);
  const [workspaceBatchMasters, setWorkspaceBatchMasters] = useState([]);
  const [workspaceBatchLocationFilter, setWorkspaceBatchLocationFilter] = useState("");
  const [workspaceMovementRows, setWorkspaceMovementRows] = useState([]);
  const [workspaceMovementTypeFilter, setWorkspaceMovementTypeFilter] = useState("");
  const [workspaceMovementBatchId, setWorkspaceMovementBatchId] = useState("");
  const [workspaceBatchPaging, setWorkspaceBatchPaging] = useState({ scope: "", page: 1, pageSize: DEFAULT_WORKSPACE_DETAIL_PAGE_SIZE });
  const [workspaceMovementPaging, setWorkspaceMovementPaging] = useState({ scope: "", page: 1, pageSize: DEFAULT_WORKSPACE_DETAIL_PAGE_SIZE });
  const [workspaceDetailRequestKey, setWorkspaceDetailRequestKey] = useState("");
  const [workspaceContextError, setWorkspaceContextError] = useState(null);

  // Each cockpit view owns its filters so context actions do not accidentally
  // alter a different tab. Text search is debounced before backend requests.
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);
  const [locationFilter, setLocationFilter] = useState("");
  const [batchQuery, setBatchQuery] = useState("");
  const debouncedBatchQuery = useDebouncedValue(batchQuery, 300);
  const [batchLocationFilter, setBatchLocationFilter] = useState("");

  // Independent server-side pagination for the two potentially long datasets.
  const [batchPage, setBatchPage] = useState(1);
  const [batchPageSize, setBatchPageSize] = useState(DEFAULT_STOCK_PAGE_SIZE);
  const [batchPagination, setBatchPagination] = useState(() => createEmptyPagination());
  const [batchProductFilter, setBatchProductFilter] = useState(null);
  const [movementPage, setMovementPage] = useState(1);
  const [movementPageSize, setMovementPageSize] = useState(DEFAULT_STOCK_PAGE_SIZE);
  const [movementPagination, setMovementPagination] = useState(() => createEmptyPagination());
  const [movementProductFilter, setMovementProductFilter] = useState(null);
  const [movementLocationFilter, setMovementLocationFilter] = useState("");
  const [movementTypeFilter, setMovementTypeFilter] = useState("");
  const [movementBatchFilter, setMovementBatchFilter] = useState(null);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [correctionMovement, setCorrectionMovement] = useState(null);
  const [lastPostingResult, setLastPostingResult] = useState(null);

  // UI request state is dataset-specific: loading a history page must not block
  // the entire cockpit or make unrelated tables look unavailable.
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [workspaceProductsLoading, setWorkspaceProductsLoading] = useState(false);
  const [workspaceBatchLoading, setWorkspaceBatchLoading] = useState(false);
  const [workspaceMovementLoading, setWorkspaceMovementLoading] = useState(false);
  const [batchBalancesLoading, setBatchBalancesLoading] = useState(false);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Lazy-tab cache invalidation. Request keys prevent needless refetches when a
  // user simply moves between tabs; posting marks both derived datasets stale.
  const [batchStale, setBatchStale] = useState(true);
  const [movementsStale, setMovementsStale] = useState(true);
  const [balanceRefreshVersion, setBalanceRefreshVersion] = useState(0);
  const [batchRefreshVersion, setBatchRefreshVersion] = useState(0);
  const [movementRefreshVersion, setMovementRefreshVersion] = useState(0);
  const [workspaceRefreshVersion, setWorkspaceRefreshVersion] = useState(0);
  const [workspaceDetailRefreshVersion, setWorkspaceDetailRefreshVersion] = useState(0);
  const lastBatchRequestKeyRef = useRef("");
  const lastMovementRequestKeyRef = useRef("");
  const locationsRequestIdRef = useRef(0);
  const balancesRequestIdRef = useRef(0);
  const batchRequestIdRef = useRef(0);
  const movementRequestIdRef = useRef(0);
  const workspaceProductsRequestIdRef = useRef(0);
  const workspaceBatchRequestIdRef = useRef(0);
  const [workspaceDetail, setWorkspaceDetail] = useState(null);
  const postingBaselineRef = useRef("");

  // Single reusable movement form. The selected action controls which fields are
  // required and which backend endpoint is called.
  const [action, setAction] = useState(ACTION_RECEIVE);
  const [form, setForm] = useState(() => createInitialStockForm());

  const loadBalances = useCallback(async (options = {}) => {
    const requestId = ++balancesRequestIdRef.current;

    try {
      setBalancesLoading(true);
      const rows = await fetchStockBalances(
        {
          q: debouncedQuery.trim() || undefined,
          location_id: locationFilter || undefined,
        },
        options
      );
      if (balancesRequestIdRef.current === requestId) {
        setBalances(rows);
      }
      return rows;
    } catch (err) {
      if (balancesRequestIdRef.current === requestId && !isRequestCanceled(err)) {
        setError(err?.message || t("stock:overview.loadError"));
      }
      return null;
    } finally {
      if (balancesRequestIdRef.current === requestId) {
        setBalancesLoading(false);
      }
    }
  }, [debouncedQuery, locationFilter, t]);

  // Locations are master data and are no longer reloaded when search/filter text
  // changes. A language change may recreate t and therefore perform one benign
  // refresh, but normal cockpit interaction does not.
  useEffect(() => {
    const controller = new AbortController();

    const requestId = ++locationsRequestIdRef.current;

    (async () => {
      try {
        setLocationsLoading(true);
        const rows = await fetchStockLocations({}, { signal: controller.signal });
        if (locationsRequestIdRef.current === requestId) {
          setLocations(rows);
        }
      } catch (err) {
        if (locationsRequestIdRef.current === requestId && !isRequestCanceled(err)) {
          setError(err?.message || t("stock:overview.loadError"));
        }
      } finally {
        if (locationsRequestIdRef.current === requestId) {
          setLocationsLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [t]);

  // The product workspace has its own paged master dataset, so the legacy
  // location-balance table is now lazy as well. It loads only when one of the
  // global views is opened, where the global KPI strip is also visible.
  useEffect(() => {
    if (activeView === VIEW_PRODUCT_WORKSPACE) return undefined;
    const controller = new AbortController();
    loadBalances({ signal: controller.signal });
    return () => controller.abort();
  }, [activeView, loadBalances, balanceRefreshVersion]);

  // Product master browser. It is the default stock workspace and therefore
  // loads immediately, but remains independent from the heavier LOT/movement
  // previews below. The first visible product becomes active automatically.
  useEffect(() => {
    if (activeView !== VIEW_PRODUCT_WORKSPACE) return undefined;

    const controller = new AbortController();
    const requestId = ++workspaceProductsRequestIdRef.current;

    (async () => {
      try {
        setWorkspaceProductsLoading(true);
        setError("");
        const result = await fetchStockWorkspaceProducts(
          {
            q: debouncedWorkspaceProductQuery.trim() || undefined,
            location_id: workspaceLocationFilter || undefined,
            only_with_stock: workspaceOnlyWithStock ? "true" : undefined,
            page: workspaceProductPage,
            page_size: workspaceProductPageSize,
          },
          { signal: controller.signal }
        );
        if (workspaceProductsRequestIdRef.current !== requestId) return;

        const items = result?.items || [];
        const nextPagination = result?.pagination || createEmptyPagination(workspaceProductPageSize);
        setWorkspaceProducts(items);
        setWorkspaceProductPagination(nextPagination);

        const resolvedPage = Number(nextPagination.page || 1);
        if (resolvedPage !== workspaceProductPage) setWorkspaceProductPage(resolvedPage);

        setActiveWorkspaceProduct((current) => {
          const refreshed = current
            ? items.find((item) => String(item.id) === String(current.id))
            : null;
          return refreshed || items[0] || null;
        });
      } catch (err) {
        if (workspaceProductsRequestIdRef.current === requestId && !isRequestCanceled(err)) {
          setError(err?.message || t("stock:overview.loadError"));
        }
      } finally {
        if (workspaceProductsRequestIdRef.current === requestId) setWorkspaceProductsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [
    activeView,
    debouncedWorkspaceProductQuery,
    workspaceLocationFilter,
    workspaceOnlyWithStock,
    workspaceProductPage,
    workspaceProductPageSize,
    workspaceRefreshVersion,
    t,
  ]);

  const activeWorkspaceProductId = activeWorkspaceProduct?.id ? String(activeWorkspaceProduct.id) : "";

  // Initial load and an empty product search legitimately have no selection.
  // Missing IDs must not match each other. Keep product and read metadata from
  // the same verified context; never fall back to a stale browser-row snapshot.
  const activeWorkspaceDetail =
    activeWorkspaceProductId &&
    workspaceDetail?.product?.id != null &&
    String(workspaceDetail.product.id) === activeWorkspaceProductId
      ? workspaceDetail
      : null;

  // Reset pagination by the actual filter scope, not a second delayed fetch.
  // This also covers automatic first-product selection after a master search.
  const workspaceProductContextIdRef = useRef(activeWorkspaceProductId);
  const effectiveWorkspaceMovementBatchId = workspaceProductContextIdRef.current === activeWorkspaceProductId
    ? workspaceMovementBatchId : "";
  useEffect(() => {
    if (workspaceProductContextIdRef.current !== activeWorkspaceProductId) {
      workspaceProductContextIdRef.current = activeWorkspaceProductId;
      setWorkspaceMovementBatchId("");
      setSelectedMovement(null);
    }
  }, [activeWorkspaceProductId]);
  const workspaceBatchScope = JSON.stringify([activeWorkspaceProductId, workspaceBatchLocationFilter]);
  const workspaceMovementScope = JSON.stringify([activeWorkspaceProductId, workspaceMovementTypeFilter,
    effectiveWorkspaceMovementBatchId, workspaceMovementPeriod.from, workspaceMovementPeriod.to]);
  const workspaceBatchPage = workspaceBatchPaging.scope === workspaceBatchScope ? workspaceBatchPaging.page : 1;
  const workspaceMovementPage = workspaceMovementPaging.scope === workspaceMovementScope ? workspaceMovementPaging.page : 1;
  useEffect(() => {
    setWorkspaceBatchPaging(prev => prev.scope === workspaceBatchScope ? prev : { ...prev, scope: workspaceBatchScope, page: 1 });
  }, [workspaceBatchScope]);
  useEffect(() => {
    setWorkspaceMovementPaging(prev => prev.scope === workspaceMovementScope ? prev : { ...prev, scope: workspaceMovementScope, page: 1 });
  }, [workspaceMovementScope]);

  const workspaceContextParams = useMemo(() => ({
    batch_page: workspaceBatchPage,
    batch_page_size: workspaceBatchPaging.pageSize,
    movement_page: workspaceMovementPage,
    movement_page_size: workspaceMovementPaging.pageSize,
    batch_location_id: workspaceBatchLocationFilter || undefined,
    movement_type: workspaceMovementTypeFilter || undefined,
    batch_id: effectiveWorkspaceMovementBatchId || undefined,
    business_date_from: workspaceMovementPeriod.from || undefined,
    business_date_to: workspaceMovementPeriod.to || undefined,
  }), [workspaceBatchPage, workspaceBatchPaging.pageSize, workspaceMovementPage, workspaceMovementPaging.pageSize,
    workspaceBatchLocationFilter, workspaceMovementTypeFilter, effectiveWorkspaceMovementBatchId,
    workspaceMovementPeriod.from, workspaceMovementPeriod.to]);
  const workspaceContextRequestKey = JSON.stringify([activeWorkspaceProductId, workspaceContextParams, workspaceDetailRefreshVersion]);
  const latestWorkspaceContextKeyRef = useRef(workspaceContextRequestKey);
  latestWorkspaceContextKeyRef.current = workspaceContextRequestKey;
  const workspaceContextReady = Boolean(activeWorkspaceDetail && workspaceDetailRequestKey === workspaceContextRequestKey);
  const workspaceContextErrorMessage = workspaceContextError?.key === workspaceContextRequestKey ? workspaceContextError.message : "";
  const workspaceContextPending = Boolean(activeWorkspaceProductId) && !workspaceContextReady && !workspaceContextErrorMessage;

  // One HTTP request = one repeatable-read product/LOT/ledger snapshot. Keep the
  // same product header mounted while changing pages; hide mismatched rows and
  // counts immediately, including the render before effect cleanup runs.
  useEffect(() => {
    const requestId = ++workspaceBatchRequestIdRef.current;
    setWorkspaceContextError(null);
    setWorkspaceBatchRows([]); setWorkspaceMovementRows([]);
    if (activeView !== VIEW_PRODUCT_WORKSPACE || !activeWorkspaceProductId) {
      setWorkspaceDetail(null); setWorkspaceDetailRequestKey(""); setWorkspaceBatchMasters([]);
      setWorkspaceBatchLoading(false); setWorkspaceMovementLoading(false); return undefined;
    }
    const controller = new AbortController();
    const isCurrent = () => requestId === workspaceBatchRequestIdRef.current &&
      !controller.signal.aborted && latestWorkspaceContextKeyRef.current === workspaceContextRequestKey;
    setWorkspaceBatchLoading(true); setWorkspaceMovementLoading(true);
    fetchStockProductContext(activeWorkspaceProductId, workspaceContextParams, { signal: controller.signal }).then(result => {
      if (!isCurrent()) return;
      if (result?.product?.id == null || String(result.product.id) !== activeWorkspaceProductId) {
        throw new Error(t("stock:overview.loadError"));
      }
      // Detect a mixed deployment (old endpoint silently fixed at 8/12). Never
      // present those rows as a working page-size selector with unreachable data.
      if ((result.batches?.pagination?.page_size != null && Number(result.batches.pagination.page_size) !== workspaceContextParams.batch_page_size) ||
          (result.movements?.pagination?.page_size != null && Number(result.movements.pagination.page_size) !== workspaceContextParams.movement_page_size)) {
        throw new Error(t("stock:workspace.paginationUnavailable"));
      }
      setWorkspaceDetail(result);
      setWorkspaceDetailRequestKey(workspaceContextRequestKey);
      setWorkspaceBatchRows(result.batches?.items || []);
      setWorkspaceBatchMasters(result.batch_options || []);
      setWorkspaceMovementRows(result.movements?.items || []);
    }).catch(err => {
      if (isCurrent() && !isRequestCanceled(err)) {
        const message = err?.message || t("stock:overview.loadError");
        setWorkspaceDetail(null); setWorkspaceDetailRequestKey(""); setWorkspaceBatchMasters([]);
        setWorkspaceContextError({ key: workspaceContextRequestKey, message });
        setError(message);
      }
    }).finally(() => {
      if (isCurrent()) { setWorkspaceBatchLoading(false); setWorkspaceMovementLoading(false); }
    });
    return () => { controller.abort(); workspaceBatchRequestIdRef.current += 1; };
  }, [activeView, activeWorkspaceProductId, workspaceContextParams, workspaceContextRequestKey, t]);

  const changeWorkspaceBatchPage = (page) => setWorkspaceBatchPaging(prev => ({
    ...prev, scope: workspaceBatchScope, page: Math.max(1, page),
  }));
  const changeWorkspaceMovementPage = (page) => setWorkspaceMovementPaging(prev => ({
    ...prev, scope: workspaceMovementScope, page: Math.max(1, page),
  }));
  const changeWorkspaceBatchPageSize = (pageSize) => {
    if (STOCK_PAGE_SIZE_OPTIONS.includes(pageSize)) setWorkspaceBatchPaging({ scope: workspaceBatchScope, page: 1, pageSize });
  };
  const changeWorkspaceMovementPageSize = (pageSize) => {
    if (STOCK_PAGE_SIZE_OPTIONS.includes(pageSize)) setWorkspaceMovementPaging({ scope: workspaceMovementScope, page: 1, pageSize });
  };

  const batchProductId = batchProductFilter?.id ? String(batchProductFilter.id) : "";
  const batchRequestKey = useMemo(() => JSON.stringify({
    q: debouncedBatchQuery.trim(),
    product: batchProductId,
    location: batchLocationFilter || "",
    page: batchPage,
    pageSize: batchPageSize,
    refresh: batchRefreshVersion,
  }), [debouncedBatchQuery, batchProductId, batchLocationFilter, batchPage, batchPageSize, batchRefreshVersion]);

  useEffect(() => {
    if (activeView !== VIEW_BATCHES) return undefined;
    if (!batchStale && lastBatchRequestKeyRef.current === batchRequestKey) return undefined;

    const controller = new AbortController();
    const requestId = ++batchRequestIdRef.current;

    (async () => {
      try {
        setBatchBalancesLoading(true);
        setError("");
        const result = await fetchStockBatchBalances(
          {
            q: debouncedBatchQuery.trim() || undefined,
            product_id: batchProductId || undefined,
            location_id: batchLocationFilter || undefined,
            page: batchPage,
            page_size: batchPageSize,
          },
          { signal: controller.signal }
        );

        if (batchRequestIdRef.current !== requestId) return;

        const nextPagination = result?.pagination || createEmptyPagination(batchPageSize);
        setBatchBalances(result?.items || []);
        setBatchPagination(nextPagination);
        setBatchStale(false);

        const resolvedPage = Number(nextPagination.page || 1);
        const resolvedKey = JSON.stringify({
          q: debouncedBatchQuery.trim(),
          product: batchProductId,
          location: batchLocationFilter || "",
          page: resolvedPage,
          pageSize: Number(nextPagination.page_size || batchPageSize),
          refresh: batchRefreshVersion,
        });
        lastBatchRequestKeyRef.current = resolvedKey;
        if (resolvedPage !== batchPage) setBatchPage(resolvedPage);
      } catch (err) {
        if (batchRequestIdRef.current === requestId && !isRequestCanceled(err)) {
          setError(err?.message || t("stock:overview.loadError"));
        }
      } finally {
        if (batchRequestIdRef.current === requestId) {
          setBatchBalancesLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [
    activeView,
    batchStale,
    batchRequestKey,
    debouncedBatchQuery,
    batchProductId,
    batchLocationFilter,
    batchPage,
    batchPageSize,
    batchRefreshVersion,
    t,
  ]);

  const movementProductId = movementProductFilter?.id ? String(movementProductFilter.id) : "";
  const movementBatchId = movementBatchFilter?.id ? String(movementBatchFilter.id) : "";
  const movementRequestKey = useMemo(() => JSON.stringify({
    product: movementProductId,
    location: movementLocationFilter || "",
    movementType: movementTypeFilter || "",
    batch: movementBatchId,
    businessDateFrom: movementPeriod.from || "",
    businessDateTo: movementPeriod.to || "",
    page: movementPage,
    pageSize: movementPageSize,
    refresh: movementRefreshVersion,
  }), [
    movementProductId,
    movementLocationFilter,
    movementTypeFilter,
    movementBatchId,
    movementPeriod.from,
    movementPeriod.to,
    movementPage,
    movementPageSize,
    movementRefreshVersion,
  ]);

  useEffect(() => {
    if (activeView !== VIEW_MOVEMENTS) return undefined;
    if (!movementsStale && lastMovementRequestKeyRef.current === movementRequestKey) return undefined;

    const controller = new AbortController();
    const requestId = ++movementRequestIdRef.current;

    (async () => {
      try {
        setMovementsLoading(true);
        setError("");
        const result = await fetchStockMovements(
          {
            product_id: movementProductId || undefined,
            location_id: movementLocationFilter || undefined,
            movement_type: movementTypeFilter || undefined,
            batch_id: movementBatchId || undefined,
            business_date_from: movementPeriod.from || undefined,
            business_date_to: movementPeriod.to || undefined,
            page: movementPage,
            page_size: movementPageSize,
          },
          { signal: controller.signal }
        );

        if (movementRequestIdRef.current !== requestId) return;

        const nextPagination = result?.pagination || createEmptyPagination(movementPageSize);
        setMovements(result?.items || []);
        setMovementPagination(nextPagination);
        setMovementsStale(false);

        const resolvedPage = Number(nextPagination.page || 1);
        const resolvedKey = JSON.stringify({
          product: movementProductId,
          location: movementLocationFilter || "",
          movementType: movementTypeFilter || "",
          batch: movementBatchId,
          businessDateFrom: movementPeriod.from || "",
          businessDateTo: movementPeriod.to || "",
          page: resolvedPage,
          pageSize: Number(nextPagination.page_size || movementPageSize),
          refresh: movementRefreshVersion,
        });
        lastMovementRequestKeyRef.current = resolvedKey;
        if (resolvedPage !== movementPage) setMovementPage(resolvedPage);
      } catch (err) {
        if (movementRequestIdRef.current === requestId && !isRequestCanceled(err)) {
          setError(err?.message || t("stock:overview.loadError"));
        }
      } finally {
        if (movementRequestIdRef.current === requestId) {
          setMovementsLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [
    activeView,
    movementsStale,
    movementRequestKey,
    movementProductId,
    movementLocationFilter,
    movementTypeFilter,
    movementBatchId,
    movementPeriod.from,
    movementPeriod.to,
    movementPage,
    movementPageSize,
    movementRefreshVersion,
    t,
  ]);

  const handleWorkspaceProductSelect = useCallback((product) => {
    setActiveWorkspaceProduct(product || null);
    setWorkspaceMovementBatchId("");
    setSelectedMovement(null);
  }, []);

  const handleWorkspaceProductQueryChange = useCallback((value) => {
    setWorkspaceProductQuery(value);
    setWorkspaceProductPage(1);
  }, []);

  const handleWorkspaceLocationChange = useCallback((value) => {
    setWorkspaceLocationFilter(value);
    setWorkspaceProductPage(1);
  }, []);

  const handleWorkspaceOnlyWithStockChange = useCallback((value) => {
    setWorkspaceOnlyWithStock(Boolean(value));
    setWorkspaceProductPage(1);
  }, []);

  const handleWorkspaceProductPageSizeChange = useCallback((nextSize) => {
    setWorkspaceProductPageSize(nextSize);
    setWorkspaceProductPage(1);
  }, []);

  const handleWorkspaceBatchLocationChange = useCallback((value) => {
    setWorkspaceBatchLocationFilter(value);
  }, []);

  const handleWorkspaceMovementTypeChange = useCallback((value) => {
    setWorkspaceMovementTypeFilter(value);
  }, []);

  const handleWorkspaceMovementBatchChange = useCallback((value) => {
    setWorkspaceMovementBatchId(value || "");
  }, []);

  const clearWorkspaceMovementFilters = useCallback(() => {
    setWorkspaceMovementPaging(prev => ({ ...prev, scope: "", page: 1 }));
    setWorkspaceMovementTypeFilter("");
    setWorkspaceMovementBatchId("");
    workspaceMovementPeriod.setMode(EVALUATION_PERIOD_TYPES.MONTH);
    workspaceMovementPeriod.setCursorDate(new Date());
  }, [workspaceMovementPeriod]);

  const handleSearchChange = useCallback((value) => {
    setQuery(value);
  }, []);

  const handleLocationFilterChange = useCallback((value) => {
    setLocationFilter(value);
  }, []);

  const handleBatchSearchChange = useCallback((value) => {
    setBatchQuery(value);
    setBatchPage(1);
  }, []);

  const handleBatchLocationFilterChange = useCallback((value) => {
    setBatchLocationFilter(value);
    setBatchPage(1);
  }, []);

  const handleMovementProductFilterChange = useCallback((product) => {
    const nextProduct = product || null;
    setMovementProductFilter(nextProduct);

    // A LOT belongs to one product. If the user deliberately switches the
    // product filter, drop an incompatible LOT context instead of returning an
    // apparently unexplained empty history.
    if (movementBatchFilter && String(movementBatchFilter.product_id || "") !== String(nextProduct?.id || "")) {
      setMovementBatchFilter(null);
    }
    setMovementPage(1);
  }, [movementBatchFilter]);

  const handleMovementLocationFilterChange = useCallback((value) => {
    setMovementLocationFilter(value);
    setMovementPage(1);
  }, []);

  const handleMovementTypeFilterChange = useCallback((value) => {
    setMovementTypeFilter(value);
    setMovementPage(1);
  }, []);

  const handleBatchPageSizeChange = useCallback((nextSize) => {
    setBatchPageSize(nextSize);
    setBatchPage(1);
  }, []);

  const handleMovementPageSizeChange = useCallback((nextSize) => {
    setMovementPageSize(nextSize);
    setMovementPage(1);
  }, []);

  const refreshCurrentView = useCallback(() => {
    setError("");
    setSuccess("");
    setBalanceRefreshVersion((value) => value + 1);

    if (activeView === VIEW_PRODUCT_WORKSPACE) {
      setWorkspaceRefreshVersion((value) => value + 1);
      setWorkspaceDetailRefreshVersion((value) => value + 1);
    }
    if (activeView === VIEW_BATCHES) {
      setBatchRefreshVersion((value) => value + 1);
      setBatchStale(true);
    }
    if (activeView === VIEW_MOVEMENTS) {
      setMovementRefreshVersion((value) => value + 1);
      setMovementsStale(true);
    }
  }, [activeView]);

  const activeLoading =
    locationsLoading ||
    balancesLoading ||
    (activeView === VIEW_PRODUCT_WORKSPACE && (workspaceProductsLoading || workspaceBatchLoading || workspaceMovementLoading)) ||
    (activeView === VIEW_BATCHES && batchBalancesLoading) ||
    (activeView === VIEW_MOVEMENTS && movementsLoading);

  // Cockpit KPIs intentionally avoid summing heterogeneous stock units (for
  // example ml + pcs + g). We show business-safe dimensions instead: distinct
  // products, stock-bearing locations and backend-calculated inventory value.
  const totals = useMemo(() => {
    const productIds = new Set();
    const locationIds = new Set();
    let value = 0;

    balances.forEach((row) => {
      if (Number(row.quantity || 0) !== 0) {
        const productId = row.product_id || row.product?.id;
        const locationId = row.location_id || row.location?.id;
        if (productId !== null && productId !== undefined) productIds.add(String(productId));
        if (locationId !== null && locationId !== undefined) locationIds.add(String(locationId));
      }
      value += Number(row.stock_value_net || 0);
    });

    return {
      products: productIds.size,
      locations: locationIds.size,
      value,
    };
  }, [balances]);

  const postingActions = useMemo(
    () => [
      { value: ACTION_RECEIVE, label: t("stock:actions.receipt"), icon: "+" },
      { value: ACTION_ISSUE, label: t("stock:actions.issue"), icon: "−" },
      { value: ACTION_TRANSFER, label: t("stock:actions.transfer"), icon: "↔" },
      { value: ACTION_CORRECTION, label: t("stock:actions.stockAdjustment"), icon: "±" },
    ],
    [t]
  );

  const viewTabs = useMemo(
    () => [
      { value: VIEW_PRODUCT_WORKSPACE, label: t("stock:overview.views.productWorkspace") },
      { value: VIEW_BALANCES, label: t("stock:overview.views.balancesByLocation") },
      { value: VIEW_BATCHES, label: t("stock:overview.views.allBatches") },
      { value: VIEW_MOVEMENTS, label: t("stock:overview.views.allMovements") },
    ],
    [t]
  );

  const movementTypeOptions = useMemo(
    () => Object.values(STOCK_MOVEMENT_TYPES).map((value) => ({
      value,
      label: movementLabel(t, value),
    })),
    [t]
  );

  // The shared period controller remains generic. These wrappers only add the
  // history-page rule that every filter/period change starts again on page 1.
  const movementPeriodController = {
    ...movementPeriod,
    setMode: (value) => {
      setMovementPage(1);
      movementPeriod.setMode(value);
    },
    goPrev: () => {
      setMovementPage(1);
      movementPeriod.goPrev();
    },
    goNext: () => {
      setMovementPage(1);
      movementPeriod.goNext();
    },
    setManualFrom: (value) => {
      setMovementPage(1);
      movementPeriod.setManualFrom(value);
    },
    setManualTo: (value) => {
      setMovementPage(1);
      movementPeriod.setManualTo(value);
    },
  };

  const clearMovementFilters = useCallback(() => {
    setMovementProductFilter(null);
    setMovementLocationFilter("");
    setMovementTypeFilter("");
    setMovementBatchFilter(null);
    setMovementPage(1);
    movementPeriod.setMode(EVALUATION_PERIOD_TYPES.MONTH);
    movementPeriod.setCursorDate(new Date());
  }, [movementPeriod]);

  const clearMovementBatchFilter = useCallback(() => {
    setMovementBatchFilter(null);
    setMovementPage(1);
    setMovementsStale(true);
  }, []);

  const clearBalanceFilters = useCallback(() => {
    setQuery("");
    setLocationFilter("");
  }, []);

  const clearBatchFilters = useCallback(() => {
    setBatchQuery("");
    setBatchLocationFilter("");
    setBatchProductFilter(null);
    setBatchPage(1);
  }, []);

  const clearBatchProductContext = useCallback(() => {
    setBatchProductFilter(null);
    setBatchPage(1);
  }, []);

  const workspaceMovementBatch = useMemo(() => (
    workspaceBatchMasters.find((batch) => String(batch.id) === String(workspaceMovementBatchId)) || null
  ), [workspaceBatchMasters, workspaceMovementBatchId]);

  const workspaceMasterLocationName = useMemo(() => (
    locations.find((location) => String(location.id) === String(workspaceLocationFilter))?.name || ""
  ), [locations, workspaceLocationFilter]);

  const showAllBatchesForWorkspaceProduct = useCallback(() => {
    if (!activeWorkspaceProduct) return;
    setBatchQuery("");
    setBatchProductFilter(activeWorkspaceProduct);
    setBatchLocationFilter(workspaceBatchLocationFilter || "");
    setBatchPage(1);
    setBatchStale(true);
    setActiveView(VIEW_BATCHES);
  }, [activeWorkspaceProduct, workspaceBatchLocationFilter]);

  const showAllMovementsForWorkspaceProduct = useCallback(() => {
    if (!activeWorkspaceProduct) return;

    // Preserve the exact workspace filters when opening the expanded audit browser.
    movementPeriod.setMode(EVALUATION_PERIOD_TYPES.MANUAL);
    movementPeriod.setManualFrom(workspaceMovementPeriod.from);
    movementPeriod.setManualTo(workspaceMovementPeriod.to);

    setMovementProductFilter(activeWorkspaceProduct);
    setMovementLocationFilter("");
    setMovementTypeFilter(workspaceMovementTypeFilter);
    setMovementBatchFilter(workspaceMovementBatch);
    setMovementPage(1);
    setMovementsStale(true);
    setSelectedMovement(null);
    setActiveView(VIEW_MOVEMENTS);
  }, [
    activeWorkspaceProduct,
    movementPeriod,
    workspaceMovementPeriod.from,
    workspaceMovementPeriod.to,
    workspaceMovementTypeFilter,
    workspaceMovementBatch,
  ]);

  const traceWorkspaceBatch = useCallback((batch) => {
    if (!batch?.id) return;
    const traceFrom = dateOnlyFromValue(batch.received_at || batch.createdAt) || workspaceMovementPeriod.from;
    workspaceMovementPeriod.setMode(EVALUATION_PERIOD_TYPES.MANUAL);
    workspaceMovementPeriod.setManualFrom(traceFrom);
    workspaceMovementPeriod.setManualTo(todayDateOnlyLocal());
    setWorkspaceMovementBatchId(String(batch.id));
    setWorkspaceMovementPaging(prev => ({ ...prev, scope: "", page: 1 }));
    window.requestAnimationFrame(() => {
      document.getElementById("stock-product-workspace-movements")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [workspaceMovementPeriod]);

  const showBatchesForBalance = useCallback((row) => {
    setBatchQuery("");
    setBatchProductFilter(row?.product || null);
    setBatchLocationFilter(row?.location_id || row?.location?.id ? String(row.location_id || row.location.id) : "");
    setBatchPage(1);
    setBatchStale(true);
    setActiveView(VIEW_BATCHES);
  }, []);

  const showMovementsForBalance = useCallback((row) => {
    setMovementProductFilter(row?.product || null);
    setMovementLocationFilter(row?.location_id || row?.location?.id ? String(row.location_id || row.location.id) : "");
    setMovementTypeFilter("");
    setMovementBatchFilter(null);
    setMovementPage(1);
    setMovementsStale(true);
    setSelectedMovement(null);
    setActiveView(VIEW_MOVEMENTS);
  }, []);

  const showMovementsForBatch = useCallback((row) => {
    const batch = row?.batch || null;
    if (!batch?.id) return;

    // LOT traceability must cover the lifetime of the batch, not only the
    // current-month default used by generic movement history. Start at the
    // receipt/business date when available and keep the user free to narrow the
    // range afterwards with the shared period picker.
    const traceFrom = dateOnlyFromValue(batch.received_at || batch.createdAt) || movementPeriod.from;
    movementPeriod.setMode(EVALUATION_PERIOD_TYPES.MANUAL);
    movementPeriod.setManualFrom(traceFrom);
    movementPeriod.setManualTo(todayDateOnlyLocal());

    setMovementProductFilter(row?.product || batch?.product || null);
    setMovementLocationFilter("");
    setMovementTypeFilter("");
    setMovementBatchFilter(batch);
    setMovementPage(1);
    setMovementsStale(true);
    setSelectedMovement(null);
    setActiveView(VIEW_MOVEMENTS);
  }, [movementPeriod]);

  const showLastPostingInHistory = useCallback(() => {
    if (!lastPostingResult) return;

    const businessDate = lastPostingResult.businessDate || todayDateOnlyLocal();
    const cursorDate = new Date(`${businessDate}T12:00:00`);
    movementPeriod.setMode(EVALUATION_PERIOD_TYPES.DAY);
    if (!Number.isNaN(cursorDate.getTime())) movementPeriod.setCursorDate(cursorDate);

    setMovementProductFilter(lastPostingResult.product || null);
    setMovementLocationFilter("");
    setMovementTypeFilter("");
    setMovementBatchFilter(null);
    setMovementPage(1);
    setMovementsStale(true);
    setSelectedMovement(null);
    setActiveView(VIEW_MOVEMENTS);
  }, [lastPostingResult, movementPeriod]);

  const activeActionLabel =
    postingActions.find((item) => item.value === action)?.label || t("stock:overview.postingForm");

  const postingDirty = useMemo(() => {
    if (!postingOpen || !postingBaselineRef.current) return false;
    return serializePostingDraft(selectedProduct, form) !== postingBaselineRef.current;
  }, [postingOpen, selectedProduct, form]);

  useEffect(() => {
    if (!postingOpen || !postingDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [postingOpen, postingDirty]);

  const openPostingDialog = useCallback((nextAction, context = {}) => {
    if (!canPostStock) return;

    const contextProduct = context.product || null;
    const nextForm = createContextPostingForm(nextAction, contextProduct, context.locationId || "");

    setAction(nextAction);
    setSelectedProduct(contextProduct);
    setForm(nextForm);
    postingBaselineRef.current = serializePostingDraft(contextProduct, nextForm);
    setError("");
    setSuccess("");
    setLastPostingResult(null);
    setPostingOpen(true);
  }, [canPostStock]);

  const openPostingFromBalance = useCallback((row, nextAction) => {
    openPostingDialog(nextAction, {
      product: row?.product || null,
      locationId: row?.location_id || row?.location?.id || "",
    });
  }, [openPostingDialog]);

  const openPostingFromWorkspace = useCallback((nextAction) => {
    openPostingDialog(nextAction, {
      product: activeWorkspaceProduct || null,
      locationId: workspaceLocationFilter || "",
    });
  }, [activeWorkspaceProduct, openPostingDialog, workspaceLocationFilter]);

  const handlePostingActionChange = useCallback((nextAction) => {
    if (nextAction === action) return;

    setForm((prev) => {
      const next = { ...prev };
      if (nextAction === ACTION_TRANSFER && !next.from_location_id && next.location_id) {
        next.from_location_id = next.location_id;
      }
      if (nextAction !== ACTION_TRANSFER && !next.location_id && next.from_location_id) {
        next.location_id = next.from_location_id;
      }
      return next;
    });
    setAction(nextAction);
  }, [action]);

  const closePostingDialog = useCallback(() => {
    if (saving) return;
    if (postingDirty && !window.confirm(t("stock:ux.discardConfirm"))) return;

    setPostingOpen(false);
    postingBaselineRef.current = "";
    setError("");
  }, [postingDirty, saving, t]);

  const pickerSearchParams = useMemo(() => {
    // Provide the picker with the most relevant location context we currently
    // know. For receipts/corrections/issues this is the selected location. For
    // transfers the source location is the one where availability matters.
    const activeFilterLocation = activeView === VIEW_PRODUCT_WORKSPACE
      ? workspaceLocationFilter
      : activeView === VIEW_BATCHES
        ? batchLocationFilter
        : activeView === VIEW_MOVEMENTS
          ? movementLocationFilter
          : locationFilter;
    const contextualLocationId =
      form.location_id || form.from_location_id || activeFilterLocation || undefined;

    return contextualLocationId ? { location_id: contextualLocationId } : {};
  }, [
    activeView,
    workspaceLocationFilter,
    form.location_id,
    form.from_location_id,
    locationFilter,
    batchLocationFilter,
    movementLocationFilter,
  ]);


  const isPackageReceiptMode = action === ACTION_RECEIVE && form.receipt_quantity_mode === RECEIPT_QUANTITY_MODES.PACKAGE;

  const packageReceiptQuantity = useMemo(() => {
    return calculatePackageStockQuantity(selectedProduct, form.package_quantity);
  }, [selectedProduct, form.package_quantity]);

  const packageReceiptPreview = useMemo(() => {
    if (!selectedProduct || !form.package_quantity) return "";
    if (packageReceiptQuantity == null) return t("stock:packages.previewUnavailable");

    return t("stock:packages.receiptPreview", {
      packages: formatDecimal(form.package_quantity, 3),
      package: formatProductPackage(selectedProduct, t),
      quantity: formatDecimal(packageReceiptQuantity, 3),
      unit: getProductUnit(selectedProduct) || selectedProduct.stock_unit_code || "pcs",
    });
  }, [selectedProduct, form.package_quantity, packageReceiptQuantity, t]);

  const receiptCostValues = useMemo(() => {
    if (action !== ACTION_RECEIVE) return null;
    return calculateReceiptCostValues(selectedProduct, form);
  }, [
    action,
    form.cost_entry_mode,
    form.package_quantity,
    form.package_unit_cost_net,
    form.quantity,
    form.receipt_quantity_mode,
    form.receipt_total_cost_net,
    form.unit_cost_net,
    selectedProduct,
  ]);

  const receiptCostPreview = useMemo(() => {
    if (!receiptCostValues) return "";
    return t("stock:costing.receiptPreview", {
      total: formatMoney(receiptCostValues.total),
      unitCost: `${formatDecimal(receiptCostValues.unitCost, 6)} PLN/${getProductUnit(selectedProduct) || selectedProduct?.stock_unit_code || "unit"}`,
      packageUnitCost: receiptCostValues.packageUnitCost == null ? "—" : formatMoney(receiptCostValues.packageUnitCost),
    });
  }, [receiptCostValues, selectedProduct, t]);

  const receiptBatchSummary = useMemo(() => {
    const rows = Array.isArray(form.receipt_batches) ? form.receipt_batches : [];
    let quantity = 0;
    let packages = 0;
    let meaningfulRows = 0;

    for (const row of rows) {
      const hasData = Boolean(
        String(row?.batch_number || "").trim() ||
        String(row?.expiry_date || "").trim() ||
        String(row?.quantity || "").trim() ||
        String(row?.package_quantity || "").trim()
      );
      if (!hasData) continue;
      meaningfulRows += 1;

      if (isPackageReceiptMode) {
        const packageQty = parseDecimalInput(row.package_quantity);
        if (packageQty && packageQty > 0) {
          packages += packageQty;
          quantity += calculatePackageStockQuantity(selectedProduct, packageQty) || 0;
        }
      } else {
        const qty = parseDecimalInput(row.quantity);
        if (qty && qty > 0) quantity += qty;
      }
    }

    const receiptQuantity = isPackageReceiptMode
      ? (packageReceiptQuantity || 0)
      : (parseDecimalInput(form.quantity) || 0);
    const receiptPackages = isPackageReceiptMode ? (parseDecimalInput(form.package_quantity) || 0) : null;

    return {
      meaningfulRows,
      quantity,
      packages,
      receiptQuantity,
      receiptPackages,
      quantityMatches: meaningfulRows === 0 || Math.abs(quantity - receiptQuantity) <= 0.000001,
      packagesMatch:
        !isPackageReceiptMode || meaningfulRows === 0 || Math.abs(packages - receiptPackages) <= 0.000001,
    };
  }, [form.receipt_batches, form.quantity, form.package_quantity, isPackageReceiptMode, packageReceiptQuantity, selectedProduct]);

  function handleProductSelected(product) {
    setSelectedProduct(product || null);
    setForm((prev) => {
      const nextQuantityMode = product && hasPackageDefinition(product)
        ? RECEIPT_QUANTITY_MODES.PACKAGE
        : RECEIPT_QUANTITY_MODES.STOCK_UNIT;
      const nextCostMode =
        prev.cost_entry_mode === COST_ENTRY_MODES.PACKAGE && !hasPackageDefinition(product)
          ? COST_ENTRY_MODES.STOCK_UNIT
          : prev.cost_entry_mode;

      return {
        ...prev,
        product_id: product?.id ? String(product.id) : "",
        receipt_quantity_mode: nextQuantityMode,
        cost_entry_mode: nextCostMode,
        receipt_batches: [createEmptyReceiptBatch()],
        batch_policy: product?.default_issue_batch_policy || "none",
      };
    });
  }

  function updateForm(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function updateReceiptBatch(index, field, value) {
    setForm((prev) => ({
      ...prev,
      receipt_batches: (prev.receipt_batches || []).map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      ),
    }));
  }

  function addReceiptBatch() {
    setForm((prev) => ({
      ...prev,
      receipt_batches: [...(prev.receipt_batches || []), createEmptyReceiptBatch()],
    }));
  }

  function removeReceiptBatch(index) {
    setForm((prev) => {
      const rows = (prev.receipt_batches || []).filter((_, rowIndex) => rowIndex !== index);
      return { ...prev, receipt_batches: rows.length ? rows : [createEmptyReceiptBatch()] };
    });
  }

  function handleReceiptQuantityModeChange(nextMode) {
    setForm((prev) => {
      const next = {
        ...prev,
        receipt_quantity_mode: nextMode,
      };

      if (nextMode === RECEIPT_QUANTITY_MODES.STOCK_UNIT) {
        const calculatedStockQuantity = calculatePackageStockQuantity(selectedProduct, prev.package_quantity);
        if (calculatedStockQuantity !== null) {
          next.quantity = formatDecimal(calculatedStockQuantity, 6);
        }
        next.receipt_batches = (prev.receipt_batches || []).map((row) => {
          const rowQuantity = calculatePackageStockQuantity(selectedProduct, row.package_quantity);
          return rowQuantity == null ? row : { ...row, quantity: formatDecimal(rowQuantity, 6) };
        });
      }

      if (nextMode === RECEIPT_QUANTITY_MODES.PACKAGE) {
        const calculatedPackageQuantity = calculatePackageQuantityFromStockQuantity(selectedProduct, prev.quantity);
        if (calculatedPackageQuantity !== null) {
          next.package_quantity = formatDecimal(calculatedPackageQuantity, 6);
        }
        next.receipt_batches = (prev.receipt_batches || []).map((row) => {
          const rowPackages = calculatePackageQuantityFromStockQuantity(selectedProduct, row.quantity);
          return rowPackages == null ? row : { ...row, package_quantity: formatDecimal(rowPackages, 6) };
        });
      }

      return next;
    });
  }

  function handleCostEntryModeChange(nextMode) {
    setForm((prev) => {
      const current = calculateReceiptCostValues(selectedProduct, prev);
      const next = { ...prev, cost_entry_mode: nextMode };
      if (!current) return next;

      if (nextMode === COST_ENTRY_MODES.STOCK_UNIT) {
        next.unit_cost_net = formatDecimal(current.unitCost, 6);
      } else if (nextMode === COST_ENTRY_MODES.PACKAGE && current.packageUnitCost != null) {
        next.package_unit_cost_net = formatDecimal(current.packageUnitCost, 6);
      } else if (nextMode === COST_ENTRY_MODES.TOTAL) {
        next.receipt_total_cost_net = Number(current.total).toFixed(2);
      }
      return next;
    });
  }

  function resetQuantitiesAfterSuccess() {
    setForm((prev) => ({
      ...prev,
      quantity: "",
      quantity_delta: "",
      unit_cost_net: "",
      reason: "",
      comment: "",
      batch_number: "",
      expiry_date: "",
      supplier_name: "",
      receipt_document_no: "",
      receipt_batches: [createEmptyReceiptBatch()],
      receipt_quantity_mode: RECEIPT_QUANTITY_MODES.STOCK_UNIT,
      package_quantity: "",
      cost_entry_mode: COST_ENTRY_MODES.STOCK_UNIT,
      package_unit_cost_net: "",
      receipt_total_cost_net: "",
    }));
  }

  /**
   * Submit one stock movement.
   *
   * The UI does only lightweight branching by action type. All important rules
   * such as moving-average valuation, negative-stock prevention and correction
   * audit requirements are enforced again on the backend.
   */
  const postingFlightRef = useRef(false);

  async function submitAction(event) {
    event.preventDefault();
    if (postingFlightRef.current) return;
    postingFlightRef.current = true;
    let committed = false;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let postingResult = null;

      if (action === ACTION_RECEIVE) {
        const receiptQuantity = isPackageReceiptMode
          ? (packageReceiptQuantity != null ? String(packageReceiptQuantity) : "")
          : form.quantity;

        const receiptBatches = (form.receipt_batches || [])
          .filter((row) =>
            String(row?.batch_number || "").trim() ||
            String(row?.expiry_date || "").trim() ||
            String(row?.quantity || "").trim() ||
            String(row?.package_quantity || "").trim()
          )
          .map((row) => ({
            batch_number: row.batch_number,
            expiry_date: row.expiry_date,
            quantity: isPackageReceiptMode ? "" : row.quantity,
            package_quantity: isPackageReceiptMode ? row.package_quantity : "",
            comment: row.comment,
          }));

        if (receiptBatches.length > 0 && (!receiptBatchSummary.quantityMatches || !receiptBatchSummary.packagesMatch)) {
          throw new Error(t("stock:batches.receiptAllocationMismatch"));
        }
        if (productRequiresBatchOnReceipt(selectedProduct) && receiptBatches.length === 0) {
          throw new Error(t("stock:batches.receiptBatchRequired"));
        }

        postingResult = await receiveStock({
          product_id: form.product_id,
          location_id: form.location_id,
          business_date: form.business_date,
          quantity: receiptQuantity,
          package_quantity: isPackageReceiptMode ? form.package_quantity : "",
          cost_entry_mode: form.cost_entry_mode,
          unit_cost_net: form.cost_entry_mode === COST_ENTRY_MODES.STOCK_UNIT ? form.unit_cost_net : "",
          package_unit_cost_net: form.cost_entry_mode === COST_ENTRY_MODES.PACKAGE ? form.package_unit_cost_net : "",
          total_cost_net: form.cost_entry_mode === COST_ENTRY_MODES.TOTAL ? form.receipt_total_cost_net : "",
          reason: form.reason || "receipt",
          comment: form.comment,
          receipt_batches: receiptBatches,
          supplier_name: form.supplier_name,
          receipt_document_no: form.receipt_document_no,
        });
        setSuccess(t("stock:overview.success"));
      }

      if (action === ACTION_ISSUE) {
        postingResult = await issueStock({
          product_id: form.product_id,
          location_id: form.location_id,
          business_date: form.business_date,
          quantity: form.quantity,
          reason: form.reason || "issue",
          comment: form.comment,
          batch_policy: form.batch_policy,
        });
        setSuccess(t("stock:overview.success"));
      }

      if (action === ACTION_TRANSFER) {
        postingResult = await transferStock({
          product_id: form.product_id,
          from_location_id: form.from_location_id,
          to_location_id: form.to_location_id,
          business_date: form.business_date,
          quantity: form.quantity,
          reason: form.reason || "transfer",
          comment: form.comment,
          batch_policy: form.batch_policy,
        });
        setSuccess(t("stock:overview.success"));
      }

      if (action === ACTION_CORRECTION) {
        postingResult = await correctStock({
          product_id: form.product_id,
          location_id: form.location_id,
          business_date: form.business_date,
          quantity_delta: form.quantity_delta,
          unit_cost_net: form.unit_cost_net,
          reason: form.reason,
          comment: form.comment,
          batch_policy: form.batch_policy,
          batch_number: form.batch_number,
          expiry_date: form.expiry_date,
          supplier_name: form.supplier_name,
          receipt_document_no: form.receipt_document_no,
        });
        setSuccess(t("stock:overview.success"));
      }

      committed = true;
      if (postingResult?.posting_operation?.replayed) setSuccess(t("stock:postingSafety.replayedSnapshot"));
      postingBaselineRef.current = "";
      setPostingOpen(false);
      setLastPostingResult(buildPostingResultContext({
        action,
        product: selectedProduct,
        form,
        result: postingResult,
        locations,
      }));
      resetQuantitiesAfterSuccess();
      if (activeView === VIEW_PRODUCT_WORKSPACE) {
        // Keep the now-lazy global balance dataset stale until a global view is
        // opened; the product workspace refreshes from its own authoritative
        // paged endpoint below.
        setBalanceRefreshVersion((value) => value + 1);
      } else {
        await loadBalances();
      }
      setBatchStale(true);
      setMovementsStale(true);
      setWorkspaceRefreshVersion((value) => value + 1);
      setWorkspaceDetailRefreshVersion((value) => value + 1);
      if (activeView === VIEW_BATCHES) setBatchRefreshVersion((value) => value + 1);
      if (activeView === VIEW_MOVEMENTS) setMovementRefreshVersion((value) => value + 1);
      postingBaselineRef.current = "";
      setPostingOpen(false);
    } catch (err) {
      setError(committed ? t("stock:postingSafety.refreshFailed") : t(`stock:postingSafety.codes.${err?.code}`, { defaultValue: err?.message || t("common:errors.actionFailed") }));
    } finally {
      postingFlightRef.current = false;
      setSaving(false);
    }
  }

  const openMovementCorrection = useCallback((movement) => {
    if (!canCorrectRecordedMovement || !movement?.correction_policy?.allowed) return;
    setSelectedMovement(null);
    setCorrectionMovement(movement);
    setError("");
    setSuccess("");
  }, [canCorrectRecordedMovement]);

  const movementCorrectionApplied = useCallback(async (result) => {
    const movementId = result?.movement?.id || correctionMovement?.id || "—";
    setCorrectionMovement(null);
    setSelectedMovement(null);
    setError("");
    setSuccess(t("stock:movementCorrection.success", { id: movementId }));
    setBatchStale(true);
    setMovementsStale(true);
    setBalanceRefreshVersion((value) => value + 1);
    setWorkspaceRefreshVersion((value) => value + 1);
    setWorkspaceDetailRefreshVersion((value) => value + 1);
    if (activeView === VIEW_BATCHES) setBatchRefreshVersion((value) => value + 1);
    if (activeView === VIEW_MOVEMENTS) setMovementRefreshVersion((value) => value + 1);
  }, [activeView, correctionMovement, t]);

  const valueAdjustmentApplied = useCallback(async (result) => {
    const movementId = result?.movement?.id || "—";
    setValueAdjustmentOpen(false);
    setError("");
    setSuccess(t("stock:valueAdjustment.success", { id: movementId }));
    setBatchStale(true);
    setMovementsStale(true);
    setBalanceRefreshVersion((value) => value + 1);
    setWorkspaceRefreshVersion((value) => value + 1);
    setWorkspaceDetailRefreshVersion((value) => value + 1);
    if (activeView === VIEW_BATCHES) setBatchRefreshVersion((value) => value + 1);
    if (activeView === VIEW_MOVEMENTS) setMovementRefreshVersion((value) => value + 1);
  }, [activeView, t]);

  async function recoveredPosting() {
    postingBaselineRef.current = ""; setPostingOpen(false); resetQuantitiesAfterSuccess();
    setBatchStale(true); setMovementsStale(true);
    setWorkspaceRefreshVersion(v => v + 1); setWorkspaceDetailRefreshVersion(v => v + 1);
    setBalanceRefreshVersion(v => v + 1);
    await refreshCurrentView();
  }

  return (
    <div className="stock-page">
      {!postingOpen && !correctionMovement && !valueAdjustmentOpen && canPostStock && <PendingStockOperationNotice scope="stock" onResolved={recoveredPosting} />}
      <div className="stock-page__header">
        <div>
          <p className="stock-eyebrow">{t("navigation:stock")}</p>
          <h1>{t("stock:overview.title")}</h1>
          <Link to="/stock/controls">{t("stock:control.title")}</Link>
          <p className="stock-subtitle">{t("stock:overview.cockpitSubtitle")}</p>
        </div>

        <div className="stock-page__header-actions">
          {canCorrectRecordedMovement && (
            <button type="button" className="stock-btn stock-btn--outline" onClick={() => { setValueAdjustmentOpen(true); setError(""); setSuccess(""); }}>
              {t("stock:actions.valueAdjustment")}
            </button>
          )}
          <button type="button" className="stock-btn stock-btn--secondary" onClick={refreshCurrentView} disabled={activeLoading}>
            {activeLoading ? t("common:common.loading") : t("common:actions.refresh")}
          </button>
        </div>
      </div>

      {activeView !== VIEW_PRODUCT_WORKSPACE && (
        <section className="stock-operation-bar" aria-label={t("stock:overview.operationsTitle")}>
          <div className="stock-operation-bar__copy">
            <strong>{t("stock:overview.operationsTitle")}</strong>
            <span>{t("stock:overview.operationsSubtitle")}</span>
          </div>

          {canPostStock ? (
            <div className="stock-operation-bar__actions">
              {postingActions.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={item.value === ACTION_RECEIVE ? "stock-btn stock-btn--primary" : "stock-btn stock-btn--outline"}
                  onClick={() => openPostingDialog(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="stock-readonly-badge">
              <strong>{t("stock:overview.readOnlyTitle")}</strong>
              <span>{t("stock:overview.readOnlyHint")}</span>
            </div>
          )}
        </section>
      )}

      <StockPostingResultSummary
        result={lastPostingResult}
        onDismiss={() => {
          setLastPostingResult(null);
          setSuccess("");
        }}
        onShowHistory={showLastPostingInHistory}
      />

      {activeView !== VIEW_PRODUCT_WORKSPACE && (
        <StockSummaryCards
          items={[
            { key: "products", label: t("stock:overview.productsInStock"), value: totals.products },
            { key: "locations", label: t("stock:overview.locationsWithStock"), value: totals.locations },
            { key: "value", label: t("stock:overview.totalStockValue"), value: formatMoney(totals.value) },
          ]}
        />
      )}

      {error && !postingOpen && <div className="stock-alert stock-alert--error">{error}</div>}
      {success && !lastPostingResult && <div className="stock-alert stock-alert--success">{success}</div>}

      <section className={activeView === VIEW_PRODUCT_WORKSPACE ? "stock-card stock-cockpit stock-cockpit--workspace" : "stock-card stock-cockpit"}>
        <StockViewTabs
          activeView={activeView}
          items={viewTabs}
          onChange={setActiveView}
          ariaLabel={t("stock:overview.views.ariaLabel")}
        />

        {activeView === VIEW_PRODUCT_WORKSPACE && (
          <div className="stock-tab-panel stock-tab-panel--workspace" role="tabpanel">
            <div className="stock-product-workspace-layout">
              <StockProductBrowser
                products={workspaceProducts}
                selectedProductId={activeWorkspaceProductId}
                query={workspaceProductQuery}
                onQueryChange={handleWorkspaceProductQueryChange}
                locationId={workspaceLocationFilter}
                onLocationChange={handleWorkspaceLocationChange}
                locations={locations}
                onlyWithStock={workspaceOnlyWithStock}
                onOnlyWithStockChange={handleWorkspaceOnlyWithStockChange}
                loading={workspaceProductsLoading}
                page={workspaceProductPage}
                pageCount={Number(workspaceProductPagination.page_count || 1)}
                pageSize={workspaceProductPageSize}
                pageSizeOptions={WORKSPACE_PRODUCT_PAGE_SIZE_OPTIONS}
                total={Number(workspaceProductPagination.total || 0)}
                onPageSizeChange={handleWorkspaceProductPageSizeChange}
                onPrev={() => setWorkspaceProductPage((prev) => Math.max(1, prev - 1))}
                onNext={() => setWorkspaceProductPage((prev) => Math.min(Number(workspaceProductPagination.page_count || 1), prev + 1))}
                onSelect={handleWorkspaceProductSelect}
              />

              <StockProductWorkspace
                product={activeWorkspaceDetail?.product ?? null}
                readContext={activeWorkspaceDetail?.read_context}
                contextLoading={workspaceContextPending || workspaceBatchLoading || workspaceMovementLoading}
                contextError={workspaceContextErrorMessage}
                onRetryContext={() => setWorkspaceDetailRefreshVersion(prev => prev + 1)}
                batchOptionsLimited={activeWorkspaceDetail?.batch_options_limited}
                canPost={canPostStock}
                postingActions={postingActions}
                onPost={openPostingFromWorkspace}
                masterLocationName={workspaceMasterLocationName}
                locations={locations}
                batchRows={workspaceContextReady ? workspaceBatchRows : []}
                batchLoading={workspaceContextPending || workspaceBatchLoading}
                batchPagination={workspaceContextReady ? activeWorkspaceDetail.batches?.pagination : null}
                batchPageSize={workspaceBatchPaging.pageSize}
                onBatchPageChange={changeWorkspaceBatchPage}
                onBatchPageSizeChange={changeWorkspaceBatchPageSize}
                batchLocationId={workspaceBatchLocationFilter}
                onBatchLocationChange={handleWorkspaceBatchLocationChange}
                onShowAllBatches={showAllBatchesForWorkspaceProduct}
                onTraceBatch={traceWorkspaceBatch}
                movementRows={workspaceContextReady ? workspaceMovementRows : []}
                movementLoading={workspaceContextPending || workspaceMovementLoading}
                movementPagination={workspaceContextReady ? activeWorkspaceDetail.movements?.pagination : null}
                movementPageSize={workspaceMovementPaging.pageSize}
                onMovementPageChange={changeWorkspaceMovementPage}
                onMovementPageSizeChange={changeWorkspaceMovementPageSize}
                movementPeriod={workspaceMovementPeriod}
                movementType={workspaceMovementTypeFilter}
                movementTypes={movementTypeOptions}
                onMovementTypeChange={handleWorkspaceMovementTypeChange}
                movementBatchId={effectiveWorkspaceMovementBatchId}
                movementBatchOptions={workspaceBatchMasters}
                onMovementBatchChange={handleWorkspaceMovementBatchChange}
                onClearMovementFilters={clearWorkspaceMovementFilters}
                onShowAllMovements={showAllMovementsForWorkspaceProduct}
                onOpenMovement={setSelectedMovement}
              />
            </div>
          </div>
        )}

        {activeView === VIEW_BALANCES && (
          <div className="stock-tab-panel" role="tabpanel">
            <div className="stock-card__header stock-card__header--compact">
              <div>
                <h2>{t("stock:overview.currentBalancesTitle")}</h2>
                <p>{t("stock:overview.currentBalancesSubtitle")}</p>
              </div>
            </div>

            <div className="stock-toolbar">
              <label>
                {t("common:actions.search")}
                <input
                  type="search"
                  value={query}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder={t("stock:overview.filterPlaceholder")}
                />
              </label>

              <label>
                {t("stock:overview.location")}
                <select value={locationFilter} onChange={(e) => handleLocationFilterChange(e.target.value)}>
                  <option value="">{t("stock:overview.allLocations")}</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="stock-table-wrap">
              <table className="stock-table">
                <thead>
                  <tr>
                    <th>{t("stock:overview.product")}</th>
                    <th>{t("stock:overview.location")}</th>
                    <th className="num">{t("stock:overview.quantity")}</th>
                    <th className="num">{t("stock:overview.movingAverageCost")}</th>
                    <th className="num">{t("stock:overview.stockValue")}</th>
                    <th className="stock-actions-column">{t("stock:overview.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {balances.length === 0 && (
                    <tr>
                      <td colSpan="6" className="stock-empty">
                        {balancesLoading ? (
                          t("common:common.loading")
                        ) : (
                          <div className="stock-empty-state">
                            <strong>{t("stock:ux.balanceEmptyTitle")}</strong>
                            <span>{query || locationFilter ? t("stock:ux.balanceEmptyFiltered") : t("stock:ux.balanceEmptyHint")}</span>
                            {(query || locationFilter) && (
                              <button type="button" className="stock-link-btn" onClick={clearBalanceFilters}>
                                {t("stock:ux.clearFilters")}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}

                  {balances.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.product?.name_public || "—"}</strong>
                        <span>{row.product?.variant_label || row.product?.sku || row.product?.barcode || ""}</span>
                      </td>
                      <td>{row.location?.name || "—"}</td>
                      <td className="num">
                        {formatDecimal(row.quantity, 3)} {row.stock_unit_code || getProductUnit(row.product)}
                      </td>
                      <td className="num">
                        {formatDecimal(row.average_cost_net, 4)} PLN/{row.stock_unit_code || getProductUnit(row.product)}
                      </td>
                      <td className="num">{formatMoney(row.stock_value_net)}</td>
                      <td className="stock-actions-column">
                        <StockBalanceRowActions
                          rowId={row.id}
                          canPost={canPostStock}
                          postingActions={postingActions}
                          onPost={(nextAction) => openPostingFromBalance(row, nextAction)}
                          onShowBatches={() => showBatchesForBalance(row)}
                          onShowMovements={() => showMovementsForBalance(row)}
                          menuLabel={t("stock:ux.contextMenuAria", { product: row.product?.name_public || "" })}
                          showBatchesLabel={t("stock:ux.showBatches")}
                          showMovementsLabel={t("stock:ux.showMovements")}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeView === VIEW_BATCHES && (
          <div className="stock-tab-panel" role="tabpanel">
            <div className="stock-card__header stock-card__header--compact">
              <div>
                <h2>{t("stock:batches.currentBatchBalances")}</h2>
                <p>{t("stock:batches.currentBatchBalancesSubtitle")}</p>
              </div>
            </div>

            {batchProductFilter && (
              <div className="stock-context-filter" role="status">
                <span>{t("stock:ux.productContext")}</span>
                <strong>{productLabel(batchProductFilter)}</strong>
                <button
                  type="button"
                  className="stock-context-filter__clear"
                  onClick={clearBatchProductContext}
                  aria-label={t("stock:ux.clearProductContext")}
                  title={t("stock:ux.clearProductContext")}
                >
                  ×
                </button>
              </div>
            )}

            <div className="stock-toolbar">
              <label>
                {t("common:actions.search")}
                <input
                  type="search"
                  value={batchQuery}
                  onChange={(e) => handleBatchSearchChange(e.target.value)}
                  placeholder={t("stock:overview.filterPlaceholder")}
                />
              </label>
              <label>
                {t("stock:overview.location")}
                <select value={batchLocationFilter} onChange={(e) => handleBatchLocationFilterChange(e.target.value)}>
                  <option value="">{t("stock:overview.allLocations")}</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>{location.name}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="stock-table-wrap">
              <table className="stock-table stock-table--wide">
                <thead>
                  <tr>
                    <th>{t("stock:overview.product")}</th>
                    <th>{t("stock:batches.batchNumber")}</th>
                    <th>{t("stock:batches.expiryDate")}</th>
                    <th>{t("stock:overview.location")}</th>
                    <th className="num">{t("stock:overview.quantity")}</th>
                    <th>{t("stock:packages.packageEquivalent")}</th>
                    <th className="num">{t("stock:batches.batchAverageCost")}</th>
                    <th className="num">{t("stock:batches.batchLocationValue")}</th>
                    <th>{t("stock:batches.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {batchBalances.length === 0 && (
                    <tr>
                      <td colSpan="9" className="stock-empty">
                        {batchBalancesLoading ? (
                          t("common:common.loading")
                        ) : (
                          <div className="stock-empty-state">
                            <strong>{t("stock:ux.batchEmptyTitle")}</strong>
                            <span>
                              {batchQuery || batchLocationFilter || batchProductFilter
                                ? t("stock:ux.batchEmptyFiltered")
                                : t("stock:ux.batchEmptyHint")}
                            </span>
                            {(batchQuery || batchLocationFilter || batchProductFilter) && (
                              <button type="button" className="stock-link-btn" onClick={clearBatchFilters}>
                                {t("stock:ux.clearFilters")}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}

                  {batchBalances.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.product?.name_public || "—"}</strong>
                        <span>{row.product?.sku || row.product?.barcode || ""}</span>
                      </td>
                      <td>
                        <div className="stock-batch-trace-cell">
                          <strong>{row.batch?.batch_number || "—"}</strong>
                          {row.batch?.id ? (
                            <button
                              type="button"
                              className="stock-link-btn stock-batch-trace-link"
                              onClick={() => showMovementsForBatch(row)}
                              aria-label={t("stock:batches.showMovementsAria", { batch: row.batch.batch_number || row.batch.id })}
                            >
                              {t("stock:batches.showMovements")}
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td>{formatDateOnly(row.batch?.expiry_date, i18n.resolvedLanguage || i18n.language)}</td>
                      <td>{row.location?.name || "—"}</td>
                      <td className="num">
                        {formatDecimal(row.quantity, 3)} {row.stock_unit_code || getProductUnit(row.product)}
                      </td>
                      <td>{formatBatchPackageEquivalent(row, t)}</td>
                      <td className="num">
                        {row.product?.stock_valuation_method === "batch_moving_average" && row.batch?.cost
                          ? `${formatDecimal(row.batch.cost.average_cost_net, 4)} PLN/${row.stock_unit_code || getProductUnit(row.product)}`
                          : "—"}
                      </td>
                      <td className="num">
                        {row.product?.stock_valuation_method === "batch_moving_average" && row.batch?.cost
                          ? formatMoney(Number(row.quantity || 0) * Number(row.batch.cost.average_cost_net || 0))
                          : "—"}
                      </td>
                      <td>{t(`stock:batches.statuses.${row.batch?.status || "active"}`)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <PaginationFooter
              className="stock-pagination"
              page={batchPage}
              pageCount={Number(batchPagination.page_count || 1)}
              pageSize={batchPageSize}
              pageSizeOptions={STOCK_PAGE_SIZE_OPTIONS}
              totalRows={Number(batchPagination.total || 0)}
              disabled={batchBalancesLoading}
              onPageSizeChange={handleBatchPageSizeChange}
              onPrev={() => setBatchPage((prev) => Math.max(1, prev - 1))}
              onNext={() =>
                setBatchPage((prev) => Math.min(Number(batchPagination.page_count || 1), prev + 1))
              }
            />
          </div>
        )}

        {activeView === VIEW_MOVEMENTS && (
          <div className="stock-tab-panel" role="tabpanel">
            <div className="stock-card__header stock-card__header--compact stock-audit-header">
              <div>
                <h2>{t("stock:overview.movementHistory")}</h2>
                <p>{t("stock:audit.subtitle")}</p>
              </div>
              <span className="stock-audit-header__hint">{t("stock:audit.openDetailHint")}</span>
            </div>

            <StockMovementFilters
              period={movementPeriodController}
              selectedProduct={movementProductFilter}
              onProductSelect={handleMovementProductFilterChange}
              searchProducts={searchStockProducts}
              locations={locations}
              locationId={movementLocationFilter}
              onLocationChange={handleMovementLocationFilterChange}
              movementType={movementTypeFilter}
              movementTypes={movementTypeOptions}
              onMovementTypeChange={handleMovementTypeFilterChange}
              selectedBatch={movementBatchFilter}
              onClearBatch={clearMovementBatchFilter}
              onClear={clearMovementFilters}
              loading={movementsLoading || locationsLoading}
            />

            <div className="stock-table-wrap">
              <table className="stock-table stock-table--movement-audit">
                <thead>
                  <tr>
                    <th>{t("stock:audit.columns.date")}</th>
                    <th>{t("stock:overview.type")}</th>
                    <th>{t("stock:overview.product")}</th>
                    <th>{t("stock:audit.columns.route")}</th>
                    <th className="num">{movementBatchId ? t("stock:audit.columns.batchChange") : t("stock:audit.columns.change")}</th>
                    <th className="num">{t("stock:audit.columns.stockAfter")}</th>
                    <th className="num">{t("stock:audit.columns.averageCostAfter")}</th>
                    <th className="num">{movementBatchId ? t("stock:audit.columns.batchCostValue") : t("stock:audit.columns.costValue")}</th>
                    <th>{t("stock:audit.columns.batch")}</th>
                    <th>{t("stock:audit.columns.postedBy")}</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.length === 0 && (
                    <tr>
                      <td colSpan="10" className="stock-empty">
                        {movementsLoading ? (
                          t("common:common.loading")
                        ) : (
                          <div className="stock-audit-empty">
                            <span>{t("stock:audit.empty")}</span>
                            <button type="button" className="stock-link-btn" onClick={clearMovementFilters}>
                              {t("stock:audit.clearFilters")}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}

                  {movements.map((row) => {
                    const direction = movementDirection(row);
                    const traceAllocation = getTraceBatchAllocation(row, movementBatchId);
                    const displayQuantity = movementBatchId && row.trace_batch_quantity != null
                      ? row.trace_batch_quantity
                      : row.quantity;
                    const displayValue = movementBatchId && traceAllocation
                      ? traceAllocation.totalCost
                      : row.total_cost_net;
                    const displayUnitCost = movementBatchId && traceAllocation
                      ? traceAllocation.unitCost
                      : row.unit_cost_net;
                    const unit = getMovementUnit(row);

                    return (
                      <tr
                        key={row.id}
                        className="stock-audit-row"
                        tabIndex="0"
                        role="button"
                        aria-label={t("stock:audit.openMovementAria", { id: row.id })}
                        onClick={() => setSelectedMovement(row)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setSelectedMovement(row);
                          }
                        }}
                      >
                        <td className="stock-audit-date">
                          <strong>{formatDateOnly(row.business_date, i18n.resolvedLanguage || i18n.language)}</strong>
                          <span>{t("stock:audit.registeredShort")}: {formatDateTime(row.createdAt, i18n.resolvedLanguage || i18n.language)}</span>
                        </td>
                        <td>
                          <span className="stock-audit-type">{movementLabel(t, row.movement_type)}</span>
                        </td>
                        <td>
                          <strong>{row.product?.name_public || "—"}</strong>
                          <span>{row.product?.sku || row.product?.barcode || ""}</span>
                        </td>
                        <td className="stock-audit-route">
                          <span>{row.fromLocation?.name || "—"}</span>
                          <strong aria-hidden="true">→</strong>
                          <span>{row.toLocation?.name || "—"}</span>
                        </td>
                        <td className="num">
                          <strong className={`stock-ledger-change stock-ledger-change--${direction.kind}`}>
                            {direction.prefix}{formatDecimal(displayQuantity, 3)} {unit}
                          </strong>
                        </td>
                        <td className="num">
                          <div className="stock-ledger-balance">
                            {movementBatchId && row.trace_batch_quantity_after != null ? (
                              <>
                                <strong>{formatDecimal(row.trace_batch_quantity_after, 3)} {unit}</strong>
                                <span>{t("stock:audit.traceBatchBalanceShort")}</span>
                                <small>{t("stock:audit.productBalanceShort")}: {formatDecimal(row.product_quantity_after, 3)} {unit}</small>
                              </>
                            ) : (
                              <>
                                <strong>{formatDecimal(row.product_quantity_after, 3)} {unit}</strong>
                                <span>{t("stock:audit.productBalanceShort")}</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="num">
                          {row.average_cost_after_net == null
                            ? "—"
                            : `${formatDecimal(row.average_cost_after_net, 4)} PLN/${unit || "unit"}`}
                        </td>
                        <td className="num">
                          <div className="stock-ledger-cost">
                            <strong>{displayUnitCost == null ? "—" : `${formatDecimal(displayUnitCost, 4)} PLN/${unit || "unit"}`}</strong>
                            <span>{formatMoney(displayValue)}</span>
                          </div>
                        </td>
                        <td>{formatMovementBatchSummary(row)}</td>
                        <td>
                          {formatMovementUser(row)}
                          {row.user?.role ? (
                            <span>{t(`common:roles.${row.user.role}`, { defaultValue: row.user.role })}</span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <PaginationFooter
              className="stock-pagination"
              page={movementPage}
              pageCount={Number(movementPagination.page_count || 1)}
              pageSize={movementPageSize}
              pageSizeOptions={STOCK_PAGE_SIZE_OPTIONS}
              totalRows={Number(movementPagination.total || 0)}
              disabled={movementsLoading}
              onPageSizeChange={handleMovementPageSizeChange}
              onPrev={() => setMovementPage((prev) => Math.max(1, prev - 1))}
              onNext={() =>
                setMovementPage((prev) => Math.min(Number(movementPagination.page_count || 1), prev + 1))
              }
            />
          </div>
        )}

      </section>

      <StockPostingDialog
        open={postingOpen}
        eyebrow={t("stock:overview.postingForm")}
        title={activeActionLabel}
        subtitle={t("stock:overview.postingDialogSubtitle")}
        action={action}
        actions={postingActions}
        onActionChange={handlePostingActionChange}
        onClose={closePostingDialog}
        onSubmit={submitAction}
        saving={saving}
        cancelLabel={t("common:actions.cancel")}
        submitLabel={t("common:actions.post")}
        loadingLabel={t("common:common.loading")}
        closeLabel={t("common:actions.close")}
        dirty={postingDirty}
        dirtyLabel={t("stock:ux.unsavedChanges")}
      >
        <PendingStockOperationNotice scope="stock" onResolved={recoveredPosting} />
        {error && <div className="stock-alert stock-alert--error stock-dialog__alert">{error}</div>}

        <StockFormSection
          number="1"
          title={t("stock:ux.sections.operation.title")}
          description={t("stock:ux.sections.operation.description")}
        >
          <div className="stock-form-field stock-form-field--wide">
            <ProductPicker
              label={t("stock:overview.product")}
              selectedProduct={selectedProduct}
              onSelect={handleProductSelected}
              searchProducts={searchStockProducts}
              searchParams={pickerSearchParams}
              context="stock"
              helperText={t("stock:overview.filterPlaceholder")}
            />
          </div>

          {selectedProduct && (
            <div className="stock-product-hint stock-form-field--wide">
              {t("stock:overview.stockUnit")}: <strong>{getProductUnit(selectedProduct) || "pcs"}</strong>
              <span> · </span>
              {t("stock:overview.valuationMethod")}: {" "}
              <strong>
                {t(
                  `stock:costing.productValuationMethods.${
                    selectedProduct.stock_valuation_method === "batch_moving_average"
                      ? "batch_moving_average"
                      : "product_moving_average"
                  }`
                )}
              </strong>
            </div>
          )}

          <label>
            {t("stock:fields.businessDate")}
            <input
              type="date"
              value={form.business_date}
              max={todayDateOnlyLocal()}
              onChange={(e) => updateForm("business_date", e.target.value)}
              required
            />
            <small>{t("stock:fields.businessDateHint")}</small>
          </label>

          {action !== ACTION_TRANSFER && (
            <label>
              {t("stock:overview.location")}
              <select value={form.location_id} onChange={(e) => updateForm("location_id", e.target.value)} required>
                <option value="">{t("stock:placeholders.selectLocation")}</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {action === ACTION_TRANSFER && (
            <>
              <label>
                {t("stock:fields.fromLocation")}
                <select
                  value={form.from_location_id}
                  onChange={(e) => updateForm("from_location_id", e.target.value)}
                  required
                >
                  <option value="">{t("stock:placeholders.selectSource")}</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                {t("stock:fields.toLocation")}
                <select
                  value={form.to_location_id}
                  onChange={(e) => updateForm("to_location_id", e.target.value)}
                  required
                >
                  <option value="">{t("stock:placeholders.selectTarget")}</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
        </StockFormSection>

        <StockFormSection
          number="2"
          title={t("stock:ux.sections.quantity.title")}
          description={t("stock:ux.sections.quantity.description")}
        >
          {action === ACTION_RECEIVE && (
            <label>
              {t("stock:costing.receiptQuantityMode")}
              <select
                value={form.receipt_quantity_mode}
                onChange={(e) => handleReceiptQuantityModeChange(e.target.value)}
              >
                <option value={RECEIPT_QUANTITY_MODES.STOCK_UNIT}>
                  {t("stock:costing.quantityModeStockUnit")}
                </option>
                <option value={RECEIPT_QUANTITY_MODES.PACKAGE}>
                  {t("stock:costing.quantityModePackage")}
                </option>
              </select>
              <small>{t("stock:costing.receiptQuantityModeHint")}</small>
            </label>
          )}

          {action === ACTION_RECEIVE && isPackageReceiptMode && (
            <label>
              {t("stock:packages.packageQuantity")}
              <input
                type="number"
                step="0.001"
                min="0"
                value={form.package_quantity}
                onChange={(e) => updateForm("package_quantity", e.target.value)}
                placeholder={t("stock:packages.packageQuantityPlaceholder")}
                required
              />
              {selectedProduct && (
                <small>
                  {packageReceiptPreview ||
                    t("stock:packages.packageQuantityHint", {
                      package: formatProductPackage(selectedProduct, t),
                      unit: getProductUnit(selectedProduct) || "pcs",
                    })}
                </small>
              )}
            </label>
          )}

          {action !== ACTION_CORRECTION && (
            <label>
              {action === ACTION_RECEIVE
                ? t("stock:costing.stockQuantity")
                : t("stock:overview.quantity")}
              <input
                type="number"
                step="0.001"
                min="0"
                value={
                  isPackageReceiptMode
                    ? (packageReceiptQuantity != null ? formatDecimal(packageReceiptQuantity, 6) : "")
                    : form.quantity
                }
                onChange={(e) => updateForm("quantity", e.target.value)}
                required={!isPackageReceiptMode}
                readOnly={isPackageReceiptMode}
              />
              {selectedProduct && (
                <small>
                  {isPackageReceiptMode
                    ? t("stock:costing.stockQuantityCalculatedHint", {
                        unit: getProductUnit(selectedProduct) || "pcs",
                      })
                    : t("stock:overview.quantityPostedIn", { unit: getProductUnit(selectedProduct) || "pcs" })}
                </small>
              )}
            </label>
          )}

          {action === ACTION_CORRECTION && (
            <label>
              {t("stock:overview.quantityDelta")}
              <input
                type="number"
                step="0.001"
                value={form.quantity_delta}
                onChange={(e) => updateForm("quantity_delta", e.target.value)}
                placeholder={t("stock:placeholders.quantityDeltaExample")}
                required
              />
              {selectedProduct && (
                <small>
                  {t("stock:overview.deltaPostedIn", { unit: getProductUnit(selectedProduct) || "pcs" })}
                </small>
              )}
            </label>
          )}

          {(action === ACTION_ISSUE || action === ACTION_TRANSFER) && (
            <label>
              {t("stock:batches.batchPolicy")}
              <select value={form.batch_policy} onChange={(e) => updateForm("batch_policy", e.target.value)}>
                <option value="none">{t("stock:batches.policyNone")}</option>
                <option value="FEFO">{t("stock:batches.policyFefo")}</option>
              </select>
              <small>{t("stock:batches.policyHint")}</small>
            </label>
          )}
        </StockFormSection>

        {(action === ACTION_RECEIVE || action === ACTION_CORRECTION) && (
          <StockFormSection
            number="3"
            title={t("stock:ux.sections.valuation.title")}
            description={t("stock:ux.sections.valuation.description")}
          >
            {action === ACTION_RECEIVE && (
              <>
                <label>
                  {t("stock:costing.costEntryMode")}
                  <select value={form.cost_entry_mode} onChange={(e) => handleCostEntryModeChange(e.target.value)}>
                    <option value={COST_ENTRY_MODES.STOCK_UNIT}>{t("stock:costing.modeStockUnit")}</option>
                    <option value={COST_ENTRY_MODES.PACKAGE} disabled={!hasPackageDefinition(selectedProduct)}>
                      {t("stock:costing.modePackage")}
                    </option>
                    <option value={COST_ENTRY_MODES.TOTAL}>{t("stock:costing.modeTotal")}</option>
                  </select>
                </label>

                {form.cost_entry_mode === COST_ENTRY_MODES.STOCK_UNIT && (
                  <label>
                    {t("stock:fields.unitCostNet")}
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={form.unit_cost_net}
                      onChange={(e) => updateForm("unit_cost_net", e.target.value)}
                      required
                      placeholder={t("stock:placeholders.required")}
                    />
                    {selectedProduct && (
                      <small>{t("stock:costing.stockUnitCostHint", { unit: getProductUnit(selectedProduct) || "pcs" })}</small>
                    )}
                  </label>
                )}

                {form.cost_entry_mode === COST_ENTRY_MODES.PACKAGE && (
                  <label>
                    {t("stock:costing.packageUnitCostNet")}
                    <input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={form.package_unit_cost_net}
                      onChange={(e) => updateForm("package_unit_cost_net", e.target.value)}
                      required
                      placeholder={t("stock:placeholders.required")}
                    />
                    <small>{t("stock:costing.packageCostHint")}</small>
                  </label>
                )}

                {form.cost_entry_mode === COST_ENTRY_MODES.TOTAL && (
                  <label>
                    {t("stock:costing.totalCostNet")}
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.receipt_total_cost_net}
                      onChange={(e) => updateForm("receipt_total_cost_net", e.target.value)}
                      required
                      placeholder={t("stock:placeholders.required")}
                    />
                    <small>{t("stock:costing.totalCostHint")}</small>
                  </label>
                )}

                {receiptCostPreview && <p className="stock-inline-help stock-form-field--wide">{receiptCostPreview}</p>}
              </>
            )}

            {action === ACTION_CORRECTION && (
              <label>
                {t("stock:fields.unitCostNet")}
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={form.unit_cost_net}
                  onChange={(e) => updateForm("unit_cost_net", e.target.value)}
                  placeholder={t("stock:placeholders.optionalForPositiveCorrection")}
                />
              </label>
            )}
          </StockFormSection>
        )}

        {action === ACTION_RECEIVE && (
          <StockFormSection
            number="4"
            title={t("stock:ux.sections.batches.title")}
            description={t("stock:ux.sections.batches.description")}
          >
            <div className="stock-form-field--wide stock-receipt-batches">
              {(form.receipt_batches || []).map((row, index) => {
                const calculatedRowQuantity = isPackageReceiptMode
                  ? calculatePackageStockQuantity(selectedProduct, row.package_quantity)
                  : parseDecimalInput(row.quantity);

                return (
                  <div className="stock-receipt-batch-row" key={`receipt-batch-${index}`}>
                    <div className="stock-receipt-batch-row__header">
                      <strong>{t("stock:batches.receiptBatchRow", { number: index + 1 })}</strong>
                      {(form.receipt_batches || []).length > 1 && (
                        <button
                          type="button"
                          className="stock-link-btn stock-link-btn--danger"
                          onClick={() => removeReceiptBatch(index)}
                        >
                          {t("stock:batches.removeBatch")}
                        </button>
                      )}
                    </div>

                    <label>
                      {t("stock:batches.batchNumber")}
                      <input
                        type="text"
                        value={row.batch_number}
                        onChange={(e) => updateReceiptBatch(index, "batch_number", e.target.value)}
                        placeholder={t("stock:batches.batchNumberPlaceholder")}
                        required={productRequiresBatchOnReceipt(selectedProduct)}
                      />
                    </label>

                    <label>
                      {t("stock:batches.expiryDate")}
                      <input
                        type="date"
                        value={row.expiry_date}
                        onChange={(e) => updateReceiptBatch(index, "expiry_date", e.target.value)}
                        required={productRequiresExpiryOnReceipt(selectedProduct)}
                      />
                    </label>

                    {isPackageReceiptMode ? (
                      <label>
                        {t("stock:batches.allocatedPackageQuantity")}
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={row.package_quantity}
                          onChange={(e) => updateReceiptBatch(index, "package_quantity", e.target.value)}
                          placeholder={t("stock:packages.packageQuantityPlaceholder")}
                        />
                        <small>
                          {calculatedRowQuantity == null
                            ? t("stock:packages.previewUnavailable")
                            : t("stock:batches.allocatedStockQuantity", {
                                quantity: formatDecimal(calculatedRowQuantity, 3),
                                unit: getProductUnit(selectedProduct) || "pcs",
                              })}
                        </small>
                      </label>
                    ) : (
                      <label>
                        {t("stock:batches.allocatedStockQuantityLabel", { unit: getProductUnit(selectedProduct) || "pcs" })}
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={row.quantity}
                          onChange={(e) => updateReceiptBatch(index, "quantity", e.target.value)}
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>

            <button type="button" className="stock-link-btn stock-form-field--wide" onClick={addReceiptBatch}>
              {t("stock:batches.addBatch")}
            </button>

            {receiptBatchSummary.meaningfulRows > 0 && (
              <p
                className={
                  `${
                    receiptBatchSummary.quantityMatches && receiptBatchSummary.packagesMatch
                      ? "stock-batch-allocation-summary stock-batch-allocation-summary--ok"
                      : "stock-batch-allocation-summary stock-batch-allocation-summary--error"
                  } stock-form-field--wide`
                }
              >
                {t("stock:batches.receiptAllocationSummary", {
                  allocated: formatDecimal(receiptBatchSummary.quantity, 3),
                  receipt: formatDecimal(receiptBatchSummary.receiptQuantity, 3),
                  unit: getProductUnit(selectedProduct) || "pcs",
                })}
              </p>
            )}
          </StockFormSection>
        )}

        {action === ACTION_RECEIVE && (
          <StockFormSection
            number="5"
            title={t("stock:ux.sections.document.title")}
            description={t("stock:ux.sections.document.description")}
          >
            <label>
              {t("stock:batches.supplier")}
              <input
                type="text"
                value={form.supplier_name}
                onChange={(e) => updateForm("supplier_name", e.target.value)}
                placeholder={t("stock:placeholders.optional")}
              />
            </label>

            <label>
              {t("stock:batches.receiptDocument")}
              <input
                type="text"
                value={form.receipt_document_no}
                onChange={(e) => updateForm("receipt_document_no", e.target.value)}
                placeholder={t("stock:placeholders.optional")}
              />
            </label>
          </StockFormSection>
        )}

        <StockFormSection
          number={action === ACTION_RECEIVE ? "6" : action === ACTION_CORRECTION ? "4" : "3"}
          title={t("stock:ux.sections.notes.title")}
          description={t("stock:ux.sections.notes.description")}
        >
          <label>
            {t("stock:fields.reason")}
            <input
              type="text"
              value={form.reason}
              onChange={(e) => updateForm("reason", e.target.value)}
              required={action === ACTION_CORRECTION}
              placeholder={action === ACTION_CORRECTION ? t("stock:placeholders.required") : t("stock:placeholders.optional")}
            />
          </label>

          <label>
            {t("stock:fields.comment")}
            <textarea
              rows="3"
              value={form.comment}
              onChange={(e) => updateForm("comment", e.target.value)}
              required={action === ACTION_CORRECTION}
              placeholder={action === ACTION_CORRECTION ? t("stock:placeholders.requiredForCorrections") : t("stock:placeholders.optional")}
            />
          </label>
        </StockFormSection>
      </StockPostingDialog>

      <StockValueAdjustmentDialog
        open={valueAdjustmentOpen}
        initialProduct={activeWorkspaceProduct || selectedProduct || null}
        onClose={() => setValueAdjustmentOpen(false)}
        onApplied={valueAdjustmentApplied}
      />

      <StockMovementDetailDialog
        open={Boolean(selectedMovement)}
        movement={selectedMovement}
        canCorrect={canCorrectRecordedMovement}
        onCorrect={openMovementCorrection}
        onClose={() => setSelectedMovement(null)}
      />

      <StockMovementCorrectionDialog
        open={Boolean(correctionMovement)}
        movement={correctionMovement}
        locations={locations}
        onClose={() => setCorrectionMovement(null)}
        onApplied={movementCorrectionApplied}
      />
    </div>
  );
}

export default StockOverviewPage;

